import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useForm, type Control, type FieldErrors, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import {
  ArrowDown,
  ArrowUp,
  Bot,
  Check,
  ChevronDown,
  ChevronLeft,
  ClipboardList,
  GripVertical,
  Info,
  Palette,
  Plus,
  Settings,
  ScrollText,
  ShieldCheck,
  Sparkles,
  Wrench,
  X,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/atoms/command';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/atoms/form';
import { Input } from '@/components/atoms/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Sheet, SheetContent, SheetFooter } from '@/components/atoms/sheet';
import { Switch } from '@/components/atoms/switch';
import { Textarea } from '@/components/atoms/textarea';
import { CollapsibleSectionHeader, FormAccordionCard } from '@/components/molecules';
import { EstimateLineItemsSection } from '@/components/organisms/estimates/EstimateLineItemsSection';
import { LineItemPicker } from '@/components/organisms/LineItemPicker';
import { BusinessUnitMultiSelect } from '@/components/organisms/business-units/BusinessUnitMultiSelect';
import { TagSelector } from '@/components/organisms/TagSelector';
import {
  UnifiedChecklistCreateSheet,
  type CreatedLinkDefaults,
} from '@/components/forms-engine/UnifiedChecklistCreateSheet';
import { SkillCreateDialog } from '@/components/organisms/skills/SkillCreateDialog';
import { useJobPipelines } from '@/hooks/pipelines/useJobPipelines';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { usePaymentSettings } from '@/hooks/settings/usePaymentSettings';
import { useSchedulerTechnicians } from '@/hooks/scheduler/useSchedulerTechnicians';
import { useEstimateLineItemState } from '@/hooks/estimates/useEstimateLineItemState';
import { useEstimateFinancialCalculations } from '@/hooks/estimates/useEstimateFinancialCalculations';
import { existingLineItemToEditable } from '@/routes/_dashboard/dashboard/estimates/_shared';
import type { EditableLineItem } from '@/routes/_dashboard/dashboard/estimates/_shared';
import {
  checklistLinksForJobType,
  deriveShortCodeFromName,
  isGeneralServiceSkill,
  type JobTypeChecklistDefinition,
  MAX_IRIS_SURVEY_QUESTION_LENGTH,
  MAX_IRIS_SURVEY_QUESTIONS,
  newIrisSurveyQuestionId,
  normalizeJobTypeRecurrenceFrequency,
  ON_DUTY_REQUIRES_SKILL_MESSAGE,
  readIrisListingFields,
  readIrisSurveyQuestions,
  readServiceCallFeePolicyField,
  recurringEligibleDaysPerCycle,
  REQUIRED_SKILL_MESSAGE,
  resolveJobTypeRecurrenceCadence,
  SERVICE_CALL_ONE_OFF_MESSAGE,
  specificRequiredSkills,
  supportsRouteBasedRecurringPlacement,
} from './jobTypeFormUtils';
import { apiPost, operationsClient } from '@/lib/api';
import { toast } from 'sonner';
import type { JobType, JobPriority, RequiredSkillDraft } from '@/types/settings';
import type { FormAudience, FormAutoAttach, FormSurface, FormTemplate } from '@/types/formsEngine';
import type { ServiceType } from '@/types/scheduler';
import { NOTIFI_PASTEL_COLORS } from '@/lib/design/brand-colors';
import {
  DEFAULT_JOB_TYPE_ICON_NAME,
  JOB_TYPE_ICON_OPTIONS,
} from '@/lib/design/job-type-appearance';
import { JobTypeAppearanceTile } from '@/components/molecules/JobTypeAppearanceTile';
import { resolveJobTypeBookingType } from '@/lib/jobs/jobFormModel';
import { tradeDotClass } from '@/lib/scheduler/trade-colors';
import { visitShapeDisplayLabel } from '@/components/organisms/jobs/AppointmentScheduleFields';
import { cn } from '@/lib/utils';
import { TermsSelector } from '@/components/organisms/TermsSelector';
import {
  EMPTY_TERMS_SELECTION,
  TERMS_MODES,
  readTermsSelection,
  toTermsPayload,
} from '@/lib/terms';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';

export interface ChecklistLinkDraft {
  checklistDefinitionId: string;
  blocksCompletion: boolean;
  sortOrder: number;
  audience?: FormAudience;
  instantiationOverride?: 'ON_DEMAND' | 'AUTO_ON_ATTACH' | null;
  autoAttach?: FormAutoAttach;
}

/** Do the chosen units carry more than one customer segment? Then Iris has a
 *  question to ask, and the form says so next to the picker. */
function unitsSplitOnSegment(
  selectedIds: string[],
  units: ReadonlyArray<{ id: string; segment: 'RESIDENTIAL' | 'COMMERCIAL' }>,
): boolean {
  const segments = new Set(
    units.filter((unit) => selectedIds.includes(unit.id)).map((unit) => unit.segment),
  );
  return segments.size > 1;
}

const PRESET_COLORS = NOTIFI_PASTEL_COLORS;

const PRIORITY_OPTIONS: { value: JobPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HIGH', label: 'High' },
  { value: 'EMERGENCY', label: 'Emergency' },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

function formatTimeLabel(value?: string | null) {
  if (!value) return null;
  const [hoursText, minutesText] = value.split(':');
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

function SegmentedControl<T extends string>({
  value,
  options,
  disabled,
  onChange,
  className,
}: {
  value: T;
  options: readonly { value: T; label: string }[];
  disabled?: boolean;
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'inline-flex min-h-9 w-fit max-w-full flex-wrap items-stretch rounded-md border bg-muted/35 p-0.5',
        className,
      )}
      role="radiogroup"
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            className={cn(
              'min-w-0 whitespace-nowrap rounded-[4px] px-3 py-1.5 text-sm text-muted-foreground transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected && 'bg-background font-medium text-foreground shadow-sm',
              disabled && 'cursor-not-allowed opacity-50',
            )}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function SchedulingBranch({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('space-y-3 rounded-lg bg-muted/55 p-4', className)}>{children}</div>;
}

// Visit-price rule for service calls (the ONLY fee-policy surface).
const SERVICE_CALL_FEE_POLICY_OPTIONS: {
  value: 'CREDIT_ON_ESTIMATE' | 'WAIVE_ON_CALL' | 'NONE';
  label: string;
  helper: string;
}[] = [
  {
    value: 'CREDIT_ON_ESTIMATE',
    label: 'Credit the visit price on the estimate',
    helper: 'The credit is shown on the first estimate created from the visit.',
  },
  {
    value: 'WAIVE_ON_CALL',
    label: 'Credit the visit price on the resulting job',
    helper:
      'When the customer proceeds, the visit price comes off the work — on-site or on the job from an approved estimate.',
  },
  {
    value: 'NONE',
    label: 'The visit price is always charged',
    helper: 'The customer pays the visit price even when they proceed with the work.',
  },
];

/**
 * Iris config auto-suggestions — response of
 * POST /api/messaging/iris/job-type-suggestions (operations proxy over
 * ai-operations). Drafted from the type's NAME (+ service-call capability and
 * the internal description when present); nothing is persisted server-side.
 */
interface IrisJobTypeSuggestions {
  customerDescription?: string;
  customerPhrases?: string[];
  matchingExclusions?: string[];
  includedWork?: string[];
  excludedWork?: string[];
  faqs?: { question: string; answer: string }[];
  guidance?: string;
  warranty?: string;
  surveyQuestions?: { question: string }[];
}

function fetchIrisPolish(body: {
  text: string;
  context: 'CUSTOMER_DESCRIPTION' | 'TECH_DESCRIPTION';
  subject?: string;
}): Promise<{ polished: string }> {
  return apiPost<{ polished: string }>(
    operationsClient,
    '/api/messaging/iris/job-type-suggestions/polish',
    body,
  );
}

function fetchIrisJobTypeSuggestions(body: {
  jobTypeName: string;
  scopeMode: 'DEFINED' | 'DIAGNOSTIC';
  internalDescription?: string;
}): Promise<IrisJobTypeSuggestions> {
  return apiPost<IrisJobTypeSuggestions>(
    operationsClient,
    '/api/messaging/iris/job-type-suggestions',
    body,
  );
}

/**
 * Whether every field the suggestion endpoint can fill is still empty — the
 * auto-fire guard: activating Iris only requests a draft when there is
 * nothing it could overwrite (the button's force path has no such guard).
 */
function isIrisSuggestTargetEmpty(values: JobTypeFormValues): boolean {
  return (
    !values.irisCustomerDescription.trim() &&
    !values.customerPhrases.trim() &&
    !values.irisMatchingExclusions.trim() &&
    !values.irisIncludedWork.trim() &&
    !values.irisExcludedWork.trim() &&
    !values.irisFaqs.trim() &&
    !values.irisAdditionalGuidance.trim() &&
    !values.irisSurveyQuestions.some((row) => row.question.trim())
  );
}

// NOT-362 — inheritable workflow-default options (mirror the create-form Advanced).

const INVOICING_METHOD_OPTIONS: { value: 'PER_VISIT' | 'FIXED_PRICE'; label: string }[] = [
  { value: 'PER_VISIT', label: 'Completed visit charges' },
  { value: 'FIXED_PRICE', label: 'Fixed job total' },
];

const INVOICING_SCHEDULE_OPTIONS: {
  value: 'LAST_DAY_OF_MONTH';
  label: string;
}[] = [{ value: 'LAST_DAY_OF_MONTH', label: 'Monthly, on the last day' }];

const WEATHER_POLICY_OPTIONS: { value: 'SKIP' | 'MAKE_UP'; label: string }[] = [
  { value: 'SKIP', label: 'Skip and absorb' },
  { value: 'MAKE_UP', label: 'Make up visit' },
];

const TIME_VALUE_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

// SPEC §4.1 (binary visibility) — Iris-only content rules, collected as
// messages instead of field-level zod schemas. The field bases stay permissive
// (the same pattern the scheduling/reschedule branches use: "stale hidden
// values cannot block Save") because while Iris is deactivated the drawer
// promises the content is preserved-and-inert — and inert includes validation:
// a legacy row carrying stale/invalid Iris content must still Save through
// fields the UI renders read-only. The schema-level superRefine applies these
// rules ONLY while Iris is engaged (activation switch ON).
function irisListIssues(
  value: string,
  label: string,
  maxItems = 30,
  maxItemLength = 240,
): string[] {
  const issues: string[] = [];
  if (value.length > 8000) issues.push(`${label} are too long`);
  const items = value
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (items.length > maxItems) {
    issues.push(`Use at most ${maxItems} ${label.toLowerCase()}`);
  }
  if (items.some((item) => item.length > maxItemLength)) {
    issues.push(`Each ${label.toLowerCase()} entry must be ${maxItemLength} characters or fewer`);
  }
  return issues;
}

function customerPhrasesIssues(value: string): string[] {
  const issues: string[] = [];
  if (value.length > 3600) issues.push('Customer phrases are too long');
  const phrases = value
    .split(/[\n,]+/)
    .map((phrase) => phrase.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
  if (phrases.length > 30) issues.push('Use at most 30 customer phrases');
  if (phrases.some((phrase) => phrase.length > 120)) {
    issues.push('Each customer phrase must be 120 characters or fewer');
  }
  return issues;
}

function irisFaqIssues(value: string): string[] {
  const issues: string[] = [];
  if (value.length > 25000) issues.push('FAQs are too long');
  const lines = value
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length > 20) issues.push('Use at most 20 FAQs');
  if (lines.some((line) => !line.includes('|'))) {
    issues.push('Write each FAQ as Question | Answer');
  }
  if (
    lines.some((line) => {
      const separator = line.indexOf('|');
      if (separator < 0) return false;
      const question = line.slice(0, separator).trim();
      const answer = line.slice(separator + 1).trim();
      return !question || question.length > 240 || !answer || answer.length > 1000;
    })
  ) {
    issues.push('FAQ questions must be 1–240 characters and answers 1–1000 characters');
  }
  return issues;
}

// Decision D — Iris intake survey rules (gated like the other Iris-only
// content). Blank rows never block Save: the serializer drops them.
function irisSurveyQuestionIssues(questions: { question: string }[]): string[] {
  const issues: string[] = [];
  const filled = questions
    .map((entry) => entry.question.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
  if (filled.length > MAX_IRIS_SURVEY_QUESTIONS) {
    issues.push(`Use at most ${MAX_IRIS_SURVEY_QUESTIONS} survey questions`);
  }
  if (filled.some((question) => question.length > MAX_IRIS_SURVEY_QUESTION_LENGTH)) {
    issues.push(
      `Each survey question must be ${MAX_IRIS_SURVEY_QUESTION_LENGTH} characters or fewer`,
    );
  }
  return issues;
}

function isValidTime(value: string): boolean {
  return TIME_VALUE_PATTERN.test(value);
}

function timeToMinutes(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
}

// Exported for boundary-value contract tests; this module remains a component entry point.
// eslint-disable-next-line react-refresh/only-export-components
export const jobTypeFormSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    shortCode: z.string().min(2, 'Min 2 chars').max(10, 'Max 10 chars'),
    color: z.string().min(1),
    icon: z.string().optional(),
    pipelineId: z.string().uuid().nullable().optional(),
    // Business Units this job type rolls up to, in preference order.
    businessUnitIds: z.array(z.string().uuid()).optional(),
    // Job tags stamped onto every job created with this type.
    tagIds: z.array(z.string().uuid()).optional(),
    description: z.string().optional(),
    // Iris-only content fields keep permissive bases; their rules run from the
    // gated superRefine below so they are inert while Iris is deactivated.
    customerPhrases: z.string(),
    // SPEC §4.1 (UI, 2026-08-03, binary visibility) — the Iris activation
    // switch in the Iris section header: ON = irisMode LISTED (Iris knows the
    // service exists), OFF = NOT_LISTED (Iris has no idea it exists; the
    // config below is preserved but inert). It is the only Iris visibility
    // control — the diagnostic-fallback designation is retired.
    irisActivated: z.boolean(),
    // NOT-560 — service-call capability, distinct from the Iris visibility
    // mode above. Forces one-off scheduling (Decision C, see superRefine).
    isDiagnostic: z.boolean(),
    // Visit-price rule (pricing-section radio); only sent while the
    // service-call capability is on.
    serviceCallFeePolicy: z.enum(['CREDIT_ON_ESTIMATE', 'WAIVE_ON_CALL', 'NONE']),
    // Visit deposit — upfront deposit required to book the visit; available
    // on every job type. Permissive base; the gated rules run in superRefine.
    visitDepositEnabled: z.boolean(),
    visitDepositType: z.enum(['FLAT', 'PERCENT']),
    visitDepositValue: z.coerce.number().min(0, 'Deposit cannot be negative'),
    // Decision D — Iris intake survey. Permissive base (rules run from the
    // gated superRefine so the list is inert while Iris is deactivated).
    irisSurveyQuestions: z.array(
      z.object({
        id: z.string(),
        question: z.string(),
        type: z.enum(['TEXT', 'PHOTO']).default('TEXT'),
      }),
    ),
    // When Iris asks the intake questions relative to offering slots.
    irisSurveyTiming: z.enum(['BEFORE_SLOTS', 'AFTER_SLOT']),
    // Decision E — Iris asks about the customer's equipment on file.
    irisAskEquipment: z.boolean(),
    irisCustomerDescription: z.string(),
    irisMatchingExclusions: z.string(),
    irisIncludedWork: z.string(),
    irisExcludedWork: z.string(),
    irisFaqs: z.string(),
    irisWarranty: z.string(),
    irisAdditionalGuidance: z.string(),
    onDutySchedulingEnabled: z.boolean(),
    requiresParts: z.boolean(),
    defaultDuration: z.coerce
      .number()
      .int('Duration must be a whole number of minutes')
      .min(1, 'Duration must be at least 1 minute')
      .max(1440, 'Duration cannot exceed 24 hours'),
    defaultPriority: z.enum(['LOW', 'NORMAL', 'HIGH', 'EMERGENCY']),
    minCrewSize: z.coerce
      .number()
      .int('Crew size must be a whole number')
      .min(1, 'Crew must include at least 1 technician')
      .max(50, 'Crew cannot exceed 50 technicians'),
    recurrenceFrequency: z.enum(['NONE', 'DAY', 'WEEK', 'MONTH', 'YEAR']),
    // Scheduling branch limits are applied in superRefine. Keeping the base
    // coercion permissive prevents a stale, hidden branch from blocking Save.
    recurrenceInterval: z.coerce.number(),
    schedulingAvailability: z.enum(['ONE_OFF', 'RECURRING']),
    oneOffPatternMode: z.enum(['CHOOSE_ON_CREATE', 'PRESET']),
    oneOffScheduleMode: z.enum(['SCHEDULE_NOW', 'SCHEDULE_LATER', 'NO_VISIT']),
    oneOffVisitShape: z.enum(['TIMED', 'ALL_DAY', 'FULL_DAY', 'MULTI_DAY']),
    oneOffPreferredTimeWindow: z.enum(['ANY', 'MORNING', 'AFTERNOON', 'SPECIFIC']),
    oneOffSpecificStartTime: z.string().nullable().optional(),
    oneOffConsecutiveDays: z.coerce.number(),
    oneOffUrgency: z.enum(['STANDARD', 'URGENT', 'EMERGENCY']),
    recurringPatternMode: z.enum(['CHOOSE_ON_CREATE', 'FIXED', 'FLEXIBLE']),
    recurringVisitsPerCycle: z.coerce.number(),
    recurringDayConstraintMode: z.enum(['ANY', 'INCLUDE', 'EXCLUDE']),
    recurringAllowedWeekDays: z.array(z.number().int().min(0).max(6)),
    recurringPlacement: z.enum(['SCHEDULE_NOW', 'ALL_DAY', 'ROUTE_BASED']),
    recurringWeekDays: z.array(z.number().int().min(0).max(6)),
    recurringMonthMode: z.enum(['DAY_OF_MONTH', 'DAY_OF_WEEK']),
    recurringMonthDay: z.coerce.number(),
    recurringMonthLastDay: z.boolean(),
    recurringMonthDayOfWeek: z.coerce.number(),
    recurringMonthDayOfWeekOccurrence: z.coerce.number(),
    recurringAnnualMonth: z.coerce.number(),
    recurringAnnualDay: z.coerce.number(),
    recurringTimeMode: z.enum(['SAME', 'PER_VISIT']),
    recurringTechnicianMode: z.enum(['SAME', 'PER_VISIT']),
    recurringVisitStartTime: z.string().nullable().optional(),
    recurringVisitEndTime: z.string().nullable().optional(),
    authRequired: z.boolean(),
    authMethod: z.enum(['SIGNATURE', 'LINK', 'VERBAL']),
    signoffRequired: z.boolean(),
    signoffMethod: z.enum(['SIGNATURE', 'LINK', 'IN_PERSON']),
    cancellationWindowHours: z.coerce.number().min(0).optional().or(z.literal('')),
    cancellationFeeType: z.enum(['NONE', 'FLAT', 'PERCENT']),
    cancellationFeeAmount: z.coerce.number().min(0).optional().or(z.literal('')),
    depositRefundable: z.boolean(),
    customerRescheduleMode: z.enum(['DISABLED', 'REQUEST', 'SELF_SERVICE']),
    // Policy-specific bounds are applied below so stale hidden values cannot
    // block Save after the customer-access mode changes.
    rescheduleCutoffHours: z.coerce.number(),
    maxCustomerReschedules: z.coerce.number(),
    // NOT-362 — inheritable workflow defaults. Each is nullable: null means
    // "not set" so an untouched job type persists NULL (never a hard-coded
    // false/'' that would then cascade onto every new visit of this type).
    defaultStageId: z.string().uuid().nullable().optional(),
    schedulingNotificationPolicy: z.enum(['ASK', 'NONE', 'SMS', 'EMAIL', 'BOTH']).default('ASK'),
    confirmationMethod: z.enum(['SMS', 'EMAIL', 'BOTH']).nullable(),
    invoicingMethod: z.enum(['PER_VISIT', 'FIXED_PRICE']).nullable(),
    invoicingSchedule: z
      .enum(['ON_COMPLETION', 'LAST_DAY_OF_MONTH', 'CUSTOM_SCHEDULE', 'AS_NEEDED'])
      .nullable(),
    autoInvoice: z.boolean().nullable(),
    autoCharge: z.boolean().nullable(),
    reviewRequestDefault: z.boolean().nullable(),
    weatherPolicy: z.enum(['SKIP', 'MAKE_UP']).nullable(),
    // Terms & Conditions default for jobs of this type. A selection, not text:
    // DEFAULT re-resolves the tenant default every time a job is created.
    termsMode: z.enum(TERMS_MODES),
    termsTemplateIds: z.array(z.string().uuid()),
    termsAndConditions: z.string(),
  })
  .superRefine((values, ctx) => {
    // Decision C — the service-call capability forces one-off scheduling.
    // Same rule (and the same words) the backend 400s with, so the client
    // pre-blocks instead of round-tripping the error. NOT Iris-gated: the
    // capability is operational, not Iris-only content.
    // Decision C is enforced in handleSubmit, gated on the user actually
    // touching serviceCallEnabled/scheduling: the backend deliberately
    // tolerates pre-existing serviceCall+RECURRING rows on unrelated edits
    // (rename, archive), and a schema-level rule here blocked those saves.

    // Visit deposit — available on every job type; validated only while the
    // option is on, so a stale hidden branch never blocks Save.
    if (values.visitDepositEnabled) {
      if (!(values.visitDepositValue > 0)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['visitDepositValue'],
          message: 'Enter the deposit amount',
        });
      } else if (values.visitDepositType === 'PERCENT' && values.visitDepositValue > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['visitDepositValue'],
          message: 'A percent deposit cannot exceed 100%',
        });
      }
    }

    // SPEC §4.1 (binary visibility) — Iris-only content is validated ONLY
    // while Iris is engaged (activation switch ON). While deactivated, the
    // content is preserved-and-inert: it must never block Save through fields
    // the UI renders read-only. The values themselves are not touched here —
    // and the update serializer omits them entirely while inert, so the
    // stored row round-trips unchanged.
    if (values.irisActivated) {
      const addIrisIssues = (path: keyof typeof values, messages: string[]) => {
        for (const message of messages) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
        }
      };
      addIrisIssues('customerPhrases', customerPhrasesIssues(values.customerPhrases));
      addIrisIssues(
        'irisCustomerDescription',
        values.irisCustomerDescription.length > 2000 ? ['Customer description is too long'] : [],
      );
      addIrisIssues(
        'irisMatchingExclusions',
        irisListIssues(values.irisMatchingExclusions, 'Matching exclusions', 30, 160),
      );
      addIrisIssues('irisIncludedWork', irisListIssues(values.irisIncludedWork, 'Included work'));
      addIrisIssues('irisExcludedWork', irisListIssues(values.irisExcludedWork, 'Excluded work'));
      addIrisIssues('irisFaqs', irisFaqIssues(values.irisFaqs));
      addIrisIssues('irisSurveyQuestions', irisSurveyQuestionIssues(values.irisSurveyQuestions));
      addIrisIssues(
        'irisWarranty',
        values.irisWarranty.length > 2000 ? ['Warranty information is too long'] : [],
      );
      addIrisIssues(
        'irisAdditionalGuidance',
        values.irisAdditionalGuidance.length > 2000 ? ['Additional guidance is too long'] : [],
      );
    }

    const addNumberIssue = (
      path: keyof typeof values,
      value: number,
      minimum: number,
      maximum: number | null,
      label: string,
    ) => {
      if (!Number.isInteger(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${label} must be a whole number`,
        });
      } else if (value < minimum) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${label} must be at least ${minimum}`,
        });
      } else if (maximum !== null && value > maximum) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [path],
          message: `${label} cannot exceed ${maximum}`,
        });
      }
    };

    if (
      values.schedulingAvailability === 'ONE_OFF' &&
      values.oneOffPatternMode === 'PRESET' &&
      values.oneOffScheduleMode === 'SCHEDULE_NOW' &&
      values.oneOffVisitShape === 'MULTI_DAY'
    ) {
      addNumberIssue(
        'oneOffConsecutiveDays',
        values.oneOffConsecutiveDays,
        2,
        365,
        'Consecutive days',
      );
    }

    if (values.schedulingAvailability === 'RECURRING') {
      addNumberIssue('recurrenceInterval', values.recurrenceInterval, 1, null, 'Interval');
      if (values.recurringPatternMode === 'FLEXIBLE') {
        addNumberIssue(
          'recurringVisitsPerCycle',
          values.recurringVisitsPerCycle,
          1,
          366,
          'Visits per cycle',
        );
      }
      if (
        values.recurringPatternMode === 'FIXED' &&
        values.recurrenceFrequency === 'MONTH' &&
        values.recurringMonthMode === 'DAY_OF_MONTH' &&
        !values.recurringMonthLastDay
      ) {
        addNumberIssue('recurringMonthDay', values.recurringMonthDay, 1, 31, 'Month day');
      }
      if (
        values.recurringPatternMode === 'FIXED' &&
        values.recurrenceFrequency === 'MONTH' &&
        values.recurringMonthMode === 'DAY_OF_WEEK'
      ) {
        addNumberIssue('recurringMonthDayOfWeek', values.recurringMonthDayOfWeek, 0, 6, 'Weekday');
        addNumberIssue(
          'recurringMonthDayOfWeekOccurrence',
          values.recurringMonthDayOfWeekOccurrence,
          1,
          4,
          'Weekday occurrence',
        );
      }
      if (values.recurringPatternMode === 'FIXED' && values.recurrenceFrequency === 'YEAR') {
        addNumberIssue('recurringAnnualMonth', values.recurringAnnualMonth, 1, 12, 'Annual month');
        addNumberIssue('recurringAnnualDay', values.recurringAnnualDay, 1, 31, 'Annual day');
      }
    }

    if (values.customerRescheduleMode !== 'DISABLED') {
      addNumberIssue('rescheduleCutoffHours', values.rescheduleCutoffHours, 0, 8760, 'Cutoff');
    }
    if (values.customerRescheduleMode === 'SELF_SERVICE') {
      addNumberIssue(
        'maxCustomerReschedules',
        values.maxCustomerReschedules,
        1,
        20,
        'Reschedule limit',
      );
    }

    // A PERCENT fee is 0–100; FLAT is an unbounded dollar amount.
    if (
      values.cancellationFeeType === 'PERCENT' &&
      typeof values.cancellationFeeAmount === 'number' &&
      values.cancellationFeeAmount > 100
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cancellationFeeAmount'],
        message: 'Percent must be between 0 and 100',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode === 'FLEXIBLE' &&
      values.recurringDayConstraintMode !== 'ANY' &&
      values.recurringAllowedWeekDays.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringAllowedWeekDays'],
        message:
          values.recurringDayConstraintMode === 'INCLUDE'
            ? 'Select at least one eligible day'
            : 'Select at least one excluded day',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode === 'FLEXIBLE' &&
      values.recurringDayConstraintMode === 'EXCLUDE' &&
      values.recurringAllowedWeekDays.length === 7
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringAllowedWeekDays'],
        message: 'At least one day must remain eligible',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode === 'FLEXIBLE' &&
      values.recurrenceFrequency === 'DAY' &&
      values.recurringVisitsPerCycle > 1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringVisitsPerCycle'],
        message: 'A daily cycle can contain only one visit',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode === 'FLEXIBLE'
    ) {
      const eligibleDaysPerCycle = recurringEligibleDaysPerCycle({
        frequency: values.recurrenceFrequency,
        interval: values.recurrenceInterval,
        dayConstraintMode: values.recurringDayConstraintMode,
        allowedWeekDays: values.recurringAllowedWeekDays,
      });
      if (eligibleDaysPerCycle !== null && values.recurringVisitsPerCycle > eligibleDaysPerCycle) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['recurringVisitsPerCycle'],
          message: `This cycle has only ${eligibleDaysPerCycle} eligible ${
            eligibleDaysPerCycle === 1 ? 'day' : 'days'
          }`,
        });
      }
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode !== 'CHOOSE_ON_CREATE' &&
      values.recurrenceFrequency === 'NONE'
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurrenceFrequency'],
        message: 'Choose how often this rule repeats',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode === 'FIXED' &&
      values.recurrenceFrequency === 'WEEK' &&
      values.recurringWeekDays.length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringWeekDays'],
        message: 'Select at least one visit day',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode !== 'CHOOSE_ON_CREATE' &&
      values.recurringPlacement === 'ROUTE_BASED' &&
      !supportsRouteBasedRecurringPlacement({
        supportsRecurring: true,
        patternMode: values.recurringPatternMode,
        frequency: values.recurrenceFrequency,
      })
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringPlacement'],
        message: 'Route-based placement requires a flexible rule or a fixed weekly rule',
      });
    }
    if (
      values.schedulingAvailability === 'ONE_OFF' &&
      values.oneOffPatternMode === 'PRESET' &&
      values.oneOffScheduleMode === 'SCHEDULE_NOW' &&
      values.oneOffVisitShape === 'TIMED' &&
      values.oneOffPreferredTimeWindow === 'SPECIFIC' &&
      (!values.oneOffSpecificStartTime || !isValidTime(values.oneOffSpecificStartTime))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['oneOffSpecificStartTime'],
        message: values.oneOffSpecificStartTime
          ? 'Enter a valid time'
          : 'Choose a preferred start time',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode !== 'CHOOSE_ON_CREATE' &&
      values.recurringPlacement === 'SCHEDULE_NOW' &&
      values.recurringTimeMode === 'SAME' &&
      (!values.recurringVisitStartTime ||
        !values.recurringVisitEndTime ||
        !isValidTime(values.recurringVisitStartTime) ||
        !isValidTime(values.recurringVisitEndTime))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [
          !values.recurringVisitStartTime ? 'recurringVisitStartTime' : 'recurringVisitEndTime',
        ],
        message:
          values.recurringVisitStartTime &&
          values.recurringVisitEndTime &&
          (!isValidTime(values.recurringVisitStartTime) ||
            !isValidTime(values.recurringVisitEndTime))
            ? 'Enter valid visit times'
            : 'Set both visit start and end times',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode !== 'CHOOSE_ON_CREATE' &&
      values.recurringPlacement === 'SCHEDULE_NOW' &&
      values.recurringTimeMode === 'SAME' &&
      values.recurringVisitStartTime &&
      values.recurringVisitEndTime &&
      isValidTime(values.recurringVisitStartTime) &&
      isValidTime(values.recurringVisitEndTime) &&
      timeToMinutes(values.recurringVisitEndTime) <= timeToMinutes(values.recurringVisitStartTime)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringVisitEndTime'],
        message: 'Visit end must be after Visit start',
      });
    }
    if (
      values.schedulingAvailability === 'RECURRING' &&
      values.recurringPatternMode === 'FIXED' &&
      values.recurrenceFrequency === 'YEAR'
    ) {
      const annualDate = new Date(
        Date.UTC(2024, values.recurringAnnualMonth - 1, values.recurringAnnualDay),
      );
      if (
        annualDate.getUTCMonth() !== values.recurringAnnualMonth - 1 ||
        annualDate.getUTCDate() !== values.recurringAnnualDay
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['recurringAnnualDay'],
          message: 'Choose a valid day for this month',
        });
      }
    }
  });

export type JobTypeFormValues = z.infer<typeof jobTypeFormSchema>;

const RECURRING_RULE_FIELDS: (keyof JobTypeFormValues)[] = [
  'recurringPatternMode',
  'recurringVisitsPerCycle',
  'recurringDayConstraintMode',
  'recurringAllowedWeekDays',
  'recurringPlacement',
  'recurringWeekDays',
  'recurringMonthMode',
  'recurringMonthDay',
  'recurringMonthLastDay',
  'recurringMonthDayOfWeek',
  'recurringMonthDayOfWeekOccurrence',
  'recurringAnnualMonth',
  'recurringAnnualDay',
  'recurringTimeMode',
  'recurringTechnicianMode',
  'recurringVisitStartTime',
  'recurringVisitEndTime',
  'recurrenceFrequency',
  'recurrenceInterval',
];

type SectionKey =
  | 'required'
  | 'iris'
  | 'scheduling'
  | 'pricing'
  | 'notifications'
  | 'workflow'
  | 'gates'
  | 'terms'
  | 'skills'
  | 'checklists';

/**
 * Which sections start expanded. Both mandatory sections are open on create —
 * a required skill is as much a precondition of saving as the name is, and a
 * collapsed section hides that until Save is already refusing.
 */
function createOpenSections(mode: 'create' | 'edit'): Record<SectionKey, boolean> {
  return {
    required: true,
    iris: false,
    scheduling: false,
    pricing: false,
    notifications: false,
    workflow: false,
    gates: false,
    terms: false,
    skills: mode === 'create',
    checklists: false,
  };
}

export interface JobTypeFormContentProps {
  layout?: 'drawer' | 'page';
  mode: 'create' | 'edit';
  jobType: JobType | null;
  skills: ServiceType[];
  checklistDefinitions: JobTypeChecklistDefinition[];
  checklistsLoading?: boolean;
  isPending: boolean;
  onClose: () => void;
  onBack?: () => void;
  onCreate: (
    values: JobTypeFormValues,
    requiredSkills: RequiredSkillDraft[],
    checklistLinks: ChecklistLinkDraft[],
    lineItems: EditableLineItem[],
  ) => void;
  onUpdate: (
    jobTypeId: string,
    values: JobTypeFormValues,
    requiredSkills: RequiredSkillDraft[],
    checklistLinks: ChecklistLinkDraft[],
    lineItems: EditableLineItem[],
    // Decision C tolerance: which touched groups this save may serialize —
    // the backend treats presence as touch, so untouched groups must be
    // omitted from the PATCH or legacy enabled+recurring rows can't save.
    updateDirtyGroups: { serviceCall: boolean; scheduling: boolean },
  ) => void;
}

interface JobTypeFormDrawerProps extends JobTypeFormContentProps {
  open: boolean;
}

export function JobTypeFormDrawer({ open, onClose, ...rest }: JobTypeFormDrawerProps) {
  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent className="w-full gap-0 p-0 sm:max-w-2xl">
        <JobTypeFormContent onClose={onClose} {...rest} />
      </SheetContent>
    </Sheet>
  );
}

export function JobTypeFormContent({
  layout = 'drawer',
  mode,
  jobType,
  skills,
  checklistDefinitions,
  checklistsLoading = false,
  isPending,
  onClose,
  onBack,
  onCreate,
  onUpdate,
}: JobTypeFormContentProps) {
  const [pickerPortal, setPickerPortal] = useState<HTMLDivElement | null>(null);
  const [editingRecurringRule, setEditingRecurringRule] = useState(false);
  const [recurringRuleDrawerVisible, setRecurringRuleDrawerVisible] = useState(false);
  const [recurringRuleSnapshot, setRecurringRuleSnapshot] = useState<JobTypeFormValues | null>(
    null,
  );
  const isEdit = mode === 'edit';
  const purchasingEnabled = usePurchasingEnabled();
  const form = useForm<JobTypeFormValues, unknown, JobTypeFormValues>({
    resolver: zodResolver(jobTypeFormSchema) as Resolver<
      JobTypeFormValues,
      unknown,
      JobTypeFormValues
    >,
    defaultValues: createDefaults(),
    mode: 'onChange',
  });
  const openRecurringRuleEditor = () => {
    setRecurringRuleSnapshot(form.getValues());
    setEditingRecurringRule(true);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setRecurringRuleDrawerVisible(true));
    });
  };
  const closeRecurringRuleEditor = (restoreSnapshot: boolean) => {
    setRecurringRuleDrawerVisible(false);
    window.setTimeout(() => {
      if (restoreSnapshot && recurringRuleSnapshot) {
        for (const field of RECURRING_RULE_FIELDS) {
          form.setValue(field, recurringRuleSnapshot[field], {
            shouldDirty: true,
            shouldTouch: false,
            shouldValidate: false,
          });
        }
        form.clearErrors(RECURRING_RULE_FIELDS);
        void form.trigger(RECURRING_RULE_FIELDS);
      }
      setEditingRecurringRule(false);
      setRecurringRuleSnapshot(null);
    }, 200);
  };
  const saveRecurringRule = () => {
    const recurringRuleFieldSet = new Set<string>(RECURRING_RULE_FIELDS);
    form.clearErrors(RECURRING_RULE_FIELDS);

    const result = jobTypeFormSchema.safeParse(form.getValues());
    const recurringIssues = result.success
      ? []
      : result.error.issues.filter(
          (issue) => typeof issue.path[0] === 'string' && recurringRuleFieldSet.has(issue.path[0]),
        );

    for (const issue of recurringIssues) {
      form.setError(issue.path[0] as keyof JobTypeFormValues, {
        type: 'manual',
        message: issue.message,
      });
    }

    const firstIssueField = recurringIssues[0]?.path[0];
    if (typeof firstIssueField === 'string') {
      form.setFocus(firstIssueField as keyof JobTypeFormValues);
      return;
    }

    closeRecurringRuleEditor(false);
  };
  const schedulingAvailability = form.watch('schedulingAvailability');
  const recurrenceFrequency = form.watch('recurrenceFrequency');
  const oneOffPatternMode = form.watch('oneOffPatternMode');
  const oneOffScheduleMode = form.watch('oneOffScheduleMode');
  const oneOffVisitShape = form.watch('oneOffVisitShape');
  const oneOffPreferredTimeWindow = form.watch('oneOffPreferredTimeWindow');
  const recurringPatternMode = form.watch('recurringPatternMode');
  const recurringVisitsPerCycle = form.watch('recurringVisitsPerCycle');
  const recurringDayConstraintMode = form.watch('recurringDayConstraintMode');
  const recurringPlacement = form.watch('recurringPlacement');
  const recurringInterval = form.watch('recurrenceInterval');
  const recurringTechnicianMode = form.watch('recurringTechnicianMode');
  const recurringVisitStartTime = form.watch('recurringVisitStartTime');
  const recurringVisitEndTime = form.watch('recurringVisitEndTime');
  const recurringMonthMode = form.watch('recurringMonthMode');
  const recurringMonthLastDay = form.watch('recurringMonthLastDay');
  const customerRescheduleMode = form.watch('customerRescheduleMode');
  const recurringTimeMode = form.watch('recurringTimeMode');
  const recurringWeekDays = form.watch('recurringWeekDays');
  const recurringAllowedWeekDays = form.watch('recurringAllowedWeekDays');
  const supportsOneOff = schedulingAvailability === 'ONE_OFF';
  const supportsRecurring = schedulingAvailability === 'RECURRING';
  const recurringRuleSummary = useMemo(() => {
    if (recurringPatternMode === 'CHOOSE_ON_CREATE') return null;

    const interval = Math.max(1, Number(recurringInterval) || 1);
    const startTime = formatTimeLabel(recurringVisitStartTime);
    const endTime = formatTimeLabel(recurringVisitEndTime);
    const placement =
      recurringPlacement === 'ROUTE_BASED'
        ? 'route decides the time and technician'
        : recurringPlacement === 'ALL_DAY'
          ? 'all day'
          : startTime && endTime
            ? `${startTime}–${endTime}`
            : 'time chosen on job';
    const technician =
      recurringPlacement === 'ROUTE_BASED'
        ? ''
        : recurringTechnicianMode === 'SAME'
          ? ' · same best technician'
          : ' · best technician per visit';

    if (recurringPatternMode === 'FLEXIBLE') {
      const cycle =
        recurrenceFrequency === 'DAY'
          ? 'day'
          : recurrenceFrequency === 'WEEK'
            ? 'week'
            : recurrenceFrequency === 'MONTH'
              ? 'month'
              : 'year';
      const allowed =
        recurringDayConstraintMode === 'ANY'
          ? 'any working day'
          : recurringDayConstraintMode === 'INCLUDE'
            ? `only ${recurringAllowedWeekDays
                .map((value) => WEEKDAY_OPTIONS.find((day) => day.value === value)?.label)
                .filter(Boolean)
                .join(', ')}`
            : `any day except ${recurringAllowedWeekDays
                .map((value) => WEEKDAY_OPTIONS.find((day) => day.value === value)?.label)
                .filter(Boolean)
                .join(', ')}`;
      return {
        title: `${recurringVisitsPerCycle} visit${
          recurringVisitsPerCycle === 1 ? '' : 's'
        } every ${interval > 1 ? `${interval} ` : ''}${cycle}${interval > 1 ? 's' : ''}`,
        detail: `${allowed} · ${placement}${technician}`,
      };
    }

    const cadence =
      recurrenceFrequency === 'DAY'
        ? `Every ${interval > 1 ? `${interval} days` : 'day'}`
        : recurrenceFrequency === 'WEEK'
          ? `Every ${interval > 1 ? `${interval} weeks` : 'week'}${
              recurringWeekDays.length
                ? ` on ${recurringWeekDays
                    .map((value) => WEEKDAY_OPTIONS.find((day) => day.value === value)?.label)
                    .filter(Boolean)
                    .join(' and ')}`
                : ''
            }`
          : recurrenceFrequency === 'MONTH'
            ? `Every ${interval > 1 ? `${interval} months` : 'month'}`
            : `Every ${interval > 1 ? `${interval} years` : 'year'}`;
    return {
      title: cadence,
      detail: `${placement}${technician}`,
    };
  }, [
    recurrenceFrequency,
    recurringAllowedWeekDays,
    recurringDayConstraintMode,
    recurringPatternMode,
    recurringPlacement,
    recurringInterval,
    recurringTechnicianMode,
    recurringVisitEndTime,
    recurringVisitStartTime,
    recurringVisitsPerCycle,
    recurringWeekDays,
  ]);
  useEffect(() => {
    const normalizedFrequency = normalizeJobTypeRecurrenceFrequency(
      recurrenceFrequency,
      supportsRecurring,
    );
    if (normalizedFrequency !== recurrenceFrequency) {
      form.setValue('recurrenceFrequency', normalizedFrequency, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, recurrenceFrequency, supportsRecurring]);
  useEffect(() => {
    const routePlacementSupported = supportsRouteBasedRecurringPlacement({
      supportsRecurring,
      patternMode: recurringPatternMode,
      frequency: recurrenceFrequency,
    });
    if (recurringPlacement === 'ROUTE_BASED' && !routePlacementSupported) {
      form.setValue('recurringPlacement', 'SCHEDULE_NOW', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [form, recurrenceFrequency, recurringPatternMode, recurringPlacement, supportsRecurring]);
  const { data: pipelines = [] } = useJobPipelines();
  // NOT-542 — the BU field only appears once the tenant has ≥1 active unit
  // (zero-cost when unused).
  const activeBusinessUnitsQuery = useBusinessUnits('active');
  const activeBusinessUnits = activeBusinessUnitsQuery.data ?? [];
  const hasNoBusinessUnits = activeBusinessUnitsQuery.isSuccess && activeBusinessUnits.length === 0;
  const { data: technicians = [], isLoading: techniciansLoading } = useSchedulerTechnicians();
  // Visit-deposit links are minted through the tenant's primary payment
  // processor; without a CONNECTED one no link can exist, so the deposit
  // option is disabled with a configure-payments banner (owner, 2026-08-03).
  // While settings load, nothing is blocked — the gate only engages on a
  // LOADED "not ready" answer.
  const { data: paymentSettings } = usePaymentSettings();
  const depositPaymentsBlocked =
    paymentSettings !== undefined &&
    !(
      paymentSettings.primaryProcessor === 'stripe' &&
      paymentSettings.stripe?.status === 'connected'
    );
  // A job type's price is defined by pricebook line items — the same editor
  // Estimates/Jobs use. This is a template, so the financials run with neutral
  // args (no tax, no discount, no membership); we only need the per-line preview.
  const {
    lineItems,
    setLineItems,
    addPickedLineItem,
    removeLineItem,
    updateQuantity,
    updateName,
    updateType,
    updateUnit,
    updateBasePrice,
    updatePriceStrategy,
    updateLaborRate,
    updateEstimatedHours,
    updateDescription,
    updateMarkupPercent,
    updateCostBasis,
    updateTaxable,
    updateServiceCallCredit,
    toggleOptional,
    toggleSelected,
    reorderLineItems,
  } = useEstimateLineItemState();
  const { lineItemPreviews, membershipEligibleRowIds } = useEstimateFinancialCalculations({
    lineItems,
    taxResolution: null,
    pricingMode: 'STANDARD',
    discountValue: 0,
    discountType: '$',
    membershipInfo: null,
    depositValue: 0,
    depositType: '$',
    bookingType: 'ONE_OFF',
    recurringFrequency: 'MONTHLY',
    recurringInterval: 1,
    recurringIntervalUnit: 'MONTHS',
    recurringStartDate: '',
    recurringDaysOfWeek: [],
    recurringEndType: 'INDEFINITE',
    recurringEndAfterCount: 0,
    recurringEndOnDate: '',
  });
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(new Set());
  /**
   * "No specific skill required — any active technician qualifies." The backend
   * expresses this as the auto-managed General service skill; the form keeps
   * that skill out of the picker and offers the mode instead, so nobody can
   * pick a skill the save is going to delete.
   */
  const [anyTechnician, setAnyTechnician] = useState(false);
  const [skillsPickerOpen, setSkillsPickerOpen] = useState(false);
  /** Which description field is being AI-polished ("pulir") right now. */
  const [polishing, setPolishing] = useState<null | 'description' | 'irisCustomerDescription'>(
    null,
  );
  const polishField = async (fieldName: 'description' | 'irisCustomerDescription') => {
    const text = (form.getValues(fieldName) ?? '').trim();
    if (!text || polishing) return;
    setPolishing(fieldName);
    try {
      const { polished } = await fetchIrisPolish({
        text,
        context: fieldName === 'description' ? 'TECH_DESCRIPTION' : 'CUSTOMER_DESCRIPTION',
        subject: form.getValues('name')?.trim() || undefined,
      });
      if (polished.trim()) {
        form.setValue(fieldName, polished.trim(), { shouldDirty: true });
      }
    } catch {
      toast.error('Could not polish the text — check the AI service.');
    } finally {
      setPolishing(null);
    }
  };
  // Owner (2026-08-03): at least one required skill is mandatory — the
  // scheduling engine is skill-typed, so a skill-less type cannot be offered
  // time slots. Cleared as soon as a skill is selected.
  const [skillsError, setSkillsError] = useState<string | null>(null);
  const [skillCreateOpen, setSkillCreateOpen] = useState(false);
  const [inlineCreatedSkills, setInlineCreatedSkills] = useState<ServiceType[]>([]);
  const [selectedChecklistLinks, setSelectedChecklistLinks] = useState<ChecklistLinkDraft[]>([]);
  const [checklistsPickerOpen, setChecklistsPickerOpen] = useState(false);
  const linkSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [checklistCreateSurface, setChecklistCreateSurface] = useState<FormSurface | null>(null);
  const [inlineCreatedChecklists, setInlineCreatedChecklists] = useState<
    JobTypeChecklistDefinition[]
  >([]);
  const initializedFormKeyRef = useRef<string | null>(null);
  const [shortCodeEdited, setShortCodeEdited] = useState(false);
  // Iris auto-suggest: pending/error drive the header legend line; the
  // monotonically increasing request id discards stale responses.
  const [suggestStatus, setSuggestStatus] = useState<'idle' | 'pending' | 'error'>('idle');
  const suggestRequestRef = useRef(0);
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(() =>
    createOpenSections(mode),
  );
  // Section a blocked Save wants scrolled back into view, consumed by the
  // effect below once the expanded body has been committed to the DOM.
  const [sectionToReveal, setSectionToReveal] = useState<SectionKey | null>(null);
  const sectionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sectionToReveal) return;
    sectionsRef.current
      ?.querySelector(`[data-section="${sectionToReveal}"]`)
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setSectionToReveal(null);
  }, [sectionToReveal]);

  /**
   * Refuse a Save loudly. The offending section can be collapsed and far below
   * the sticky Save button, so setting an inline error in place reads as "the
   * button did nothing": expand the section, scroll it into view, and toast the
   * reason so the block is legible wherever the user is looking.
   */
  function blockSave(section: SectionKey, message: string) {
    setOpenSections((current) => ({ ...current, [section]: true }));
    setSectionToReveal(section);
    toast.error(message);
  }

  const allSkills = useMemo(() => {
    const byId = new Map(skills.map((skill) => [skill.id, skill]));
    for (const skill of inlineCreatedSkills) {
      byId.set(skill.id, skill);
    }
    return [...byId.values()];
  }, [inlineCreatedSkills, skills]);
  const activeSkills = useMemo(() => allSkills.filter((skill) => skill.isActive), [allSkills]);
  /**
   * The drawer renders before the service-types query resolves, and in edit
   * mode the job type's own skills are already here — so read the id from
   * either. Without this the general skill briefly looks like an ordinary one
   * on an existing "any technician" type.
   */
  const generalServiceSkillId = useMemo(
    () =>
      allSkills.find(isGeneralServiceSkill)?.id ??
      jobType?.requiredSkills.find(isGeneralServiceSkill)?.id ??
      null,
    [allSkills, jobType?.requiredSkills],
  );
  /**
   * The skills query can resolve after the user has already chosen the
   * any-technician mode. Adopt the auto-managed row when it arrives so the
   * outgoing payload reflects the mode without requiring a second toggle.
   */
  useEffect(() => {
    if (!anyTechnician || !generalServiceSkillId) return;
    setSelectedSkills((previous) => {
      if (previous.size === 1 && previous.has(generalServiceSkillId)) return previous;
      return new Set([generalServiceSkillId]);
    });
  }, [anyTechnician, generalServiceSkillId]);
  /** Chips: the skills the user chose — never the auto-managed general one. */
  const selectedSkillRows = useMemo(
    () =>
      [...selectedSkills].flatMap((skillId) => {
        const skill = allSkills.find((item) => item.id === skillId);
        return skill && !isGeneralServiceSkill(skill) ? [skill] : [];
      }),
    [allSkills, selectedSkills],
  );
  const availableSkills = useMemo(
    () =>
      activeSkills.filter(
        (skill) => !selectedSkills.has(skill.id) && !isGeneralServiceSkill(skill),
      ),
    [activeSkills, selectedSkills],
  );
  const allChecklistDefinitions = useMemo(() => {
    const byId = new Map(checklistDefinitions.map((definition) => [definition.id, definition]));
    for (const definition of inlineCreatedChecklists) {
      byId.set(definition.id, definition);
    }
    return [...byId.values()];
  }, [checklistDefinitions, inlineCreatedChecklists]);
  const selectedChecklistRows = useMemo(
    () =>
      [...selectedChecklistLinks]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .flatMap((link) => {
          const definition = allChecklistDefinitions.find(
            (item) => item.id === link.checklistDefinitionId,
          );
          return definition ? [{ definition, link }] : [];
        }),
    [allChecklistDefinitions, selectedChecklistLinks],
  );
  const availableChecklistDefinitions = useMemo(
    () =>
      allChecklistDefinitions.filter(
        (definition) =>
          !definition.isDeleted &&
          definition.status !== 'ARCHIVED' &&
          // Checklists are no longer added; the ones already linked stay.
          definition.surface !== 'CHECKLIST' &&
          !selectedChecklistLinks.some((link) => link.checklistDefinitionId === definition.id),
      ),
    [allChecklistDefinitions, selectedChecklistLinks],
  );
  const activeTechnicians = useMemo(
    () => technicians.filter((technician) => technician.isActive),
    [technicians],
  );
  /**
   * What the user actually picked. The general skill is granted to every active
   * technician, so it never narrows the roster — counting it would only make
   * the coverage badge lie when a grant has not been backfilled yet.
   */
  const specificSkillIds = useMemo(
    () => [...selectedSkills].filter((skillId) => skillId !== generalServiceSkillId),
    [generalServiceSkillId, selectedSkills],
  );
  const qualifiedTechnicianCount = useMemo(() => {
    const now = Date.now();
    return activeTechnicians.filter((technician) =>
      specificSkillIds.every((skillId) => {
        const expertise = technician.expertise?.find(
          (entry) => (entry.serviceTypeId ?? entry.serviceType?.id) === skillId,
        );
        if (!expertise) return false;
        if (expertise.expiresAt != null && new Date(expertise.expiresAt).getTime() <= now) {
          return false;
        }
        return true;
      }),
    ).length;
  }, [activeTechnicians, specificSkillIds]);
  const initialSkills = useMemo(
    () => new Set(jobType?.requiredSkills.map((skill) => skill.id) ?? []),
    [jobType?.requiredSkills],
  );
  const initialChecklistLinks = useMemo(
    () => (jobType ? checklistLinksForJobType(jobType.id, checklistDefinitions) : []),
    [checklistDefinitions, jobType],
  );
  const initialLineItems = useMemo(
    () =>
      (jobType?.lineItems ?? []).map((raw) =>
        existingLineItemToEditable(raw as Record<string, unknown>),
      ),
    [jobType?.lineItems],
  );
  const hasSkillChanges = !areSkillSetsEqual(initialSkills, selectedSkills);
  useEffect(() => {
    if (specificSkillIds.length > 0 || anyTechnician) setSkillsError(null);
  }, [anyTechnician, specificSkillIds]);
  const hasChecklistChanges = !areChecklistLinksEqual(
    initialChecklistLinks,
    selectedChecklistLinks,
  );
  const hasLineItemChanges = !areLineItemsEqual(initialLineItems, lineItems);
  const hasChanges =
    !isEdit ||
    form.formState.isDirty ||
    hasSkillChanges ||
    hasChecklistChanges ||
    hasLineItemChanges;
  const selectedColor = form.watch('color');
  const selectedIconName = form.watch('icon');
  const selectedPipelineId = form.watch('pipelineId');
  const liveValues = form.watch();
  // Binary Iris visibility: with the activation switch OFF, Iris does not
  // know this service exists — the Iris config is inert, so its inputs render
  // disabled. The stored values are deliberately kept in the form state and
  // round-trip on save, making deactivate → reactivate lossless.
  const irisConfigDisabled = !liveValues.irisActivated;
  const legacyInvoicingSchedule =
    liveValues.invoicingSchedule === 'CUSTOM_SCHEDULE' ||
    liveValues.invoicingSchedule === 'AS_NEEDED'
      ? liveValues.invoicingSchedule
      : null;
  const workflowOverrideCount = [
    supportsRecurring && liveValues.weatherPolicy != null,
    supportsRecurring &&
      (liveValues.invoicingMethod != null || liveValues.invoicingSchedule != null),
    supportsOneOff && liveValues.autoInvoice != null,
    liveValues.autoCharge != null,
    liveValues.reviewRequestDefault != null,
  ].filter(Boolean).length;
  const isPageLayout = layout === 'page';
  const selectedPipelineStages = useMemo(
    () =>
      (pipelines.find((pipeline) => pipeline.id === selectedPipelineId)?.stages ?? []).filter(
        // NOT-362 — only Draft/Scheduled stages are valid as a job's initial stage; a job
        // can't start mid-pipeline, and the backend rejects anything else.
        (stage) => stage.corePhase === 'DRAFT' || stage.corePhase === 'SCHEDULED',
      ),
    [pipelines, selectedPipelineId],
  );

  useEffect(() => {
    const initializationKey = mode === 'create' ? 'create' : (jobType?.id ?? null);
    if (!initializationKey || initializedFormKeyRef.current === initializationKey) {
      return;
    }
    initializedFormKeyRef.current = initializationKey;

    if (mode === 'create') {
      form.reset(createDefaults());
      setLineItems([]);
      setSelectedSkills(new Set());
      setAnyTechnician(false);
      setInlineCreatedSkills([]);
      setSelectedChecklistLinks([]);
      setInlineCreatedChecklists([]);
      setShortCodeEdited(false);
      setSkillsError(null);
      setOpenSections(createOpenSections('create'));
      return;
    }

    if (!jobType) return;

    const savedSchedulingAvailability = resolveJobTypeBookingType(jobType);
    const savedRecurringCadence = resolveJobTypeRecurrenceCadence({
      supportsRecurring: savedSchedulingAvailability === 'RECURRING',
      recurringDefaults: jobType.schedulingDefaults?.recurring,
      legacyFrequency: jobType.recurrenceFrequency,
      legacyInterval: jobType.recurrenceInterval,
    });

    form.reset({
      name: jobType.name,
      shortCode: jobType.shortCode,
      color: jobType.color,
      icon: jobType.icon ?? DEFAULT_JOB_TYPE_ICON_NAME,
      pipelineId: jobType.pipelineId ?? null,
      businessUnitIds: jobType.businessUnitIds ?? [],
      tagIds: jobType.tagIds ?? [],
      description: jobType.description ?? '',
      customerPhrases: (jobType.customerPhrases ?? []).join('\n'),
      // SPEC §4.1 — stored irisMode reads into the Iris activation switch
      // (legacy SERVICE_CALL rows read back as activated — migration-by-touch).
      ...readIrisListingFields(jobType),
      // NOT-560 — dual-read: older rows without the flag derive it from the
      // fallback markers (a diagnostic fallback is always service-call enabled).
      isDiagnostic:
        jobType.scopeMode === 'DIAGNOSTIC' ||
        jobType.serviceCallEnabled === true ||
        jobType.irisMode === 'SERVICE_CALL' ||
        jobType.isServiceCallFallback === true,
      // Visit-price rule: unknown stored values read back as the default
      // job-credit option.
      serviceCallFeePolicy: readServiceCallFeePolicyField(jobType.serviceCallFeePolicy),
      visitDepositEnabled: jobType.visitDepositEnabled ?? false,
      visitDepositType: jobType.visitDepositType === 'PERCENT' ? 'PERCENT' : 'FLAT',
      visitDepositValue: Number(jobType.visitDepositValue ?? 0),
      irisSurveyQuestions: readIrisSurveyQuestions(jobType.irisSurveyQuestions),
      irisSurveyTiming: jobType.irisSurveyTiming === 'AFTER_SLOT' ? 'AFTER_SLOT' : 'BEFORE_SLOTS',
      irisAskEquipment: jobType.irisAskEquipment ?? false,
      irisCustomerDescription: jobType.irisProfile?.customerDescription ?? '',
      irisMatchingExclusions: (jobType.irisProfile?.matchingExclusions ?? []).join('\n'),
      irisIncludedWork: (jobType.irisProfile?.includedWork ?? []).join('\n'),
      irisExcludedWork: (jobType.irisProfile?.excludedWork ?? []).join('\n'),
      irisFaqs: (jobType.irisProfile?.faqs ?? [])
        .map((faq) => `${faq.question} | ${faq.answer}`)
        .join('\n'),
      irisWarranty: jobType.irisProfile?.warranty ?? '',
      irisAdditionalGuidance: jobType.irisProfile?.additionalGuidance ?? '',
      onDutySchedulingEnabled: jobType.onDutySchedulingEnabled ?? false,
      requiresParts: jobType.requiresParts ?? false,
      defaultDuration: jobType.defaultDuration,
      defaultPriority: jobType.defaultPriority,
      minCrewSize: jobType.minCrewSize,
      recurrenceFrequency: savedRecurringCadence.frequency,
      recurrenceInterval: savedRecurringCadence.interval,
      schedulingAvailability: savedSchedulingAvailability,
      oneOffPatternMode: jobType.schedulingDefaults?.oneOff?.patternMode ?? 'PRESET',
      oneOffVisitShape: jobType.schedulingDefaults?.oneOff?.visitShape ?? 'TIMED',
      oneOffScheduleMode: jobType.schedulingDefaults?.oneOff?.scheduleMode ?? 'SCHEDULE_NOW',
      oneOffPreferredTimeWindow: jobType.schedulingDefaults?.oneOff?.preferredTimeWindow ?? 'ANY',
      oneOffSpecificStartTime: jobType.schedulingDefaults?.oneOff?.specificStartTime ?? null,
      oneOffConsecutiveDays: jobType.schedulingDefaults?.oneOff?.consecutiveDays ?? 2,
      oneOffUrgency: jobType.schedulingDefaults?.oneOff?.urgency ?? 'STANDARD',
      recurringPatternMode: jobType.schedulingDefaults?.recurring?.patternMode ?? 'FIXED',
      recurringVisitsPerCycle: jobType.schedulingDefaults?.recurring?.visitsPerCycle ?? 1,
      recurringDayConstraintMode:
        jobType.schedulingDefaults?.recurring?.dayConstraintMode ??
        ((jobType.schedulingDefaults?.recurring?.allowedWeekDays?.length ?? 0) > 0
          ? 'INCLUDE'
          : 'ANY'),
      recurringAllowedWeekDays: jobType.schedulingDefaults?.recurring?.allowedWeekDays ?? [],
      recurringPlacement: jobType.schedulingDefaults?.recurring?.placement ?? 'SCHEDULE_NOW',
      recurringWeekDays: jobType.schedulingDefaults?.recurring?.weekDays ?? [],
      recurringMonthMode: jobType.schedulingDefaults?.recurring?.monthMode ?? 'DAY_OF_MONTH',
      recurringMonthDay: jobType.schedulingDefaults?.recurring?.monthDay ?? 1,
      recurringMonthLastDay: jobType.schedulingDefaults?.recurring?.monthLastDay ?? false,
      recurringMonthDayOfWeek: jobType.schedulingDefaults?.recurring?.monthDayOfWeek ?? 1,
      recurringMonthDayOfWeekOccurrence:
        jobType.schedulingDefaults?.recurring?.monthDayOfWeekOccurrence ?? 1,
      recurringAnnualMonth: jobType.schedulingDefaults?.recurring?.annualMonth ?? 1,
      recurringAnnualDay: jobType.schedulingDefaults?.recurring?.annualDay ?? 1,
      recurringTimeMode: jobType.schedulingDefaults?.recurring?.timeMode ?? 'SAME',
      recurringTechnicianMode: jobType.schedulingDefaults?.recurring?.technicianMode ?? 'PER_VISIT',
      recurringVisitStartTime: jobType.schedulingDefaults?.recurring?.visitStartTime ?? '09:00',
      recurringVisitEndTime: jobType.schedulingDefaults?.recurring?.visitEndTime ?? '10:00',
      authRequired: jobType.authorizationConfig?.required ?? false,
      authMethod: 'SIGNATURE',
      signoffRequired: jobType.signoffConfig?.required ?? false,
      signoffMethod: 'SIGNATURE',
      cancellationWindowHours: jobType.cancellationWindowHours ?? '',
      cancellationFeeType: jobType.cancellationFeeType ?? 'NONE',
      cancellationFeeAmount: jobType.cancellationFeeAmount ?? '',
      depositRefundable: jobType.depositRefundable ?? true,
      customerRescheduleMode: jobType.customerRescheduleMode ?? 'REQUEST',
      rescheduleCutoffHours: jobType.rescheduleCutoffHours ?? 24,
      maxCustomerReschedules: jobType.maxCustomerReschedules ?? 1,
      defaultStageId: jobType.defaultStageId ?? null,
      schedulingNotificationPolicy: jobType.schedulingNotificationPolicy ?? 'ASK',
      confirmationMethod: jobType.confirmationMethod ?? null,
      invoicingMethod: jobType.invoicingMethod ?? null,
      invoicingSchedule: jobType.invoicingSchedule ?? null,
      autoInvoice: jobType.autoInvoice ?? null,
      autoCharge: jobType.autoCharge ?? null,
      reviewRequestDefault: jobType.reviewRequestDefault ?? null,
      weatherPolicy: jobType.weatherPolicy ?? null,
      ...readTermsSelection(jobType),
    });
    setLineItems(
      (jobType.lineItems ?? []).map((raw) =>
        existingLineItemToEditable(raw as Record<string, unknown>),
      ),
    );
    setSelectedSkills(new Set(jobType.requiredSkills.map((skill) => skill.id)));
    // A type whose only requirement is the auto-managed general skill (or that
    // carries none at all) IS the "any active technician" mode.
    setAnyTechnician(specificRequiredSkills(jobType.requiredSkills).length === 0);
    setInlineCreatedSkills([]);
    setSelectedChecklistLinks(initialChecklistLinks);
    setInlineCreatedChecklists([]);
    if (layout === 'page') {
      setOpenSections({
        required: true,
        iris: true,
        scheduling: true,
        pricing: true,
        notifications: true,
        workflow: true,
        gates: true,
        terms: true,
        skills: true,
        checklists: true,
      });
    }
  }, [form, initialChecklistLinks, jobType, layout, mode, setLineItems]);

  function toggleSection(section: SectionKey) {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  }

  /**
   * Map an Iris configuration draft into the form. A field is written only
   * when the draft has content for it AND (force mode, or the field is still
   * empty) — a non-force run never overwrites what the user typed. Survey
   * questions get fresh stable ids from the shared id helper.
   */
  function applyIrisSuggestions(result: IrisJobTypeSuggestions, force: boolean) {
    const values = form.getValues();
    const setText = (
      name: keyof JobTypeFormValues,
      current: string,
      suggested: string | undefined,
    ) => {
      const next = (suggested ?? '').trim();
      if (!next) return;
      if (!force && current.trim()) return;
      form.setValue(name, next, { shouldDirty: true, shouldValidate: true });
    };
    setText('irisCustomerDescription', values.irisCustomerDescription, result.customerDescription);
    setText('customerPhrases', values.customerPhrases, (result.customerPhrases ?? []).join('\n'));
    setText(
      'irisMatchingExclusions',
      values.irisMatchingExclusions,
      (result.matchingExclusions ?? []).join('\n'),
    );
    setText('irisIncludedWork', values.irisIncludedWork, (result.includedWork ?? []).join('\n'));
    setText('irisExcludedWork', values.irisExcludedWork, (result.excludedWork ?? []).join('\n'));
    setText(
      'irisFaqs',
      values.irisFaqs,
      (result.faqs ?? []).map((faq) => `${faq.question} | ${faq.answer}`).join('\n'),
    );
    setText('irisAdditionalGuidance', values.irisAdditionalGuidance, result.guidance);
    setText('irisWarranty', values.irisWarranty, result.warranty);
    const suggestedQuestions = (result.surveyQuestions ?? [])
      .map((entry) => entry.question.trim())
      .filter(Boolean)
      .slice(0, MAX_IRIS_SURVEY_QUESTIONS)
      .map((question) => ({ id: newIrisSurveyQuestionId(), question, type: 'TEXT' as const }));
    const hasQuestions = values.irisSurveyQuestions.some((row) => row.question.trim());
    if (suggestedQuestions.length > 0 && (force || !hasQuestions)) {
      form.setValue('irisSurveyQuestions', suggestedQuestions, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }

  /**
   * Request an Iris configuration draft for the current name. `force` is the
   * button's regenerate path (may overwrite non-empty fields); the auto-fire
   * path passes false. No name → no request (the legend explains why).
   */
  async function runIrisSuggestions(force: boolean) {
    const values = form.getValues();
    const name = values.name.trim();
    if (!name) return;
    const requestId = ++suggestRequestRef.current;
    setSuggestStatus('pending');
    try {
      const internalDescription = values.description?.trim() ?? '';
      const result = await fetchIrisJobTypeSuggestions({
        jobTypeName: name,
        scopeMode: values.isDiagnostic ? 'DIAGNOSTIC' : 'DEFINED',
        ...(internalDescription ? { internalDescription } : {}),
      });
      if (suggestRequestRef.current !== requestId) return;
      applyIrisSuggestions(result, force);
      setSuggestStatus('idle');
    } catch {
      if (suggestRequestRef.current === requestId) setSuggestStatus('error');
    }
  }

  function addSkill(id: string) {
    setSelectedSkills((previous) => {
      const next = new Set(previous);
      next.add(id);
      return next;
    });
    setSkillsPickerOpen(false);
  }

  /**
   * Switch between "any active technician" and specific skills. The two are
   * exclusive by construction on the backend — a real skill removes the general
   * link — so the form makes the trade visible instead of losing a selection at
   * save time. The general skill stays in the payload when we know its id, so
   * the mode round-trips through the same write path it always did.
   */
  function toggleAnyTechnician(next: boolean) {
    setAnyTechnician(next);
    setSelectedSkills((previous) => {
      const specific = [...previous].filter((skillId) => skillId !== generalServiceSkillId);
      if (!next) return new Set(specific);
      return new Set(generalServiceSkillId ? [generalServiceSkillId] : []);
    });
  }

  function removeSkill(id: string) {
    setSelectedSkills((previous) => {
      const next = new Set(previous);
      next.delete(id);
      return next;
    });
  }

  function handleSkillCreated(skill: ServiceType) {
    setInlineCreatedSkills((current) => {
      if (current.some((item) => item.id === skill.id)) return current;
      return [...current, skill];
    });
    setAnyTechnician(false);
    setSelectedSkills((previous) => {
      const next = new Set([...previous].filter((id) => id !== generalServiceSkillId));
      next.add(skill.id);
      return next;
    });
  }

  function handleChecklistCreated(template: FormTemplate, defaults: CreatedLinkDefaults) {
    const checklist: JobTypeChecklistDefinition = {
      id: template.id,
      name: template.name,
      description: template.description,
      version: template.version,
      status: template.status,
      isDeleted: false,
      surface: template.surface,
      publicFillable: template.publicFillable,
      blocksCompletion: defaults.blocksCompletion,
      jobTypeLinks: [],
    };
    setInlineCreatedChecklists((current) => {
      if (current.some((item) => item.id === checklist.id)) return current;
      return [...current, checklist];
    });
    // Hand the definition over rather than letting the lookup below find it:
    // the state holding it was set a line ago and this render cannot see it
    // yet, so a template just built for the customer would link as the
    // technician's.
    addChecklistDefinition(checklist.id, checklist);
    setChecklistCreateSurface(null);
  }

  function addChecklistDefinition(
    checklistDefinitionId: string,
    justCreated?: JobTypeChecklistDefinition,
  ) {
    const added =
      justCreated ?? allChecklistDefinitions.find((item) => item.id === checklistDefinitionId);
    setSelectedChecklistLinks((current) => {
      if (current.some((link) => link.checklistDefinitionId === checklistDefinitionId)) {
        return current;
      }

      return [
        ...current,
        {
          checklistDefinitionId,
          // Not required unless someone says so: switched on here, or when
          // the form was created here with "Required to complete". How it is
          // linked elsewhere says nothing about this job type.
          blocksCompletion: justCreated?.blocksCompletion ?? false,
          // Available to add unless required: a required form is always
          // there, so it starts on the job.
          autoAttach: justCreated?.blocksCompletion ? 'JOB' : null,
          sortOrder: Math.max(-1, ...current.map((link) => link.sortOrder)) + 1,
          // Follow the template's own "Completed by" choice. Pinning every new
          // link to TECHNICIAN locks the customer out of a form built for
          // them — the public route refuses an assignment whose audience is
          // not CUSTOMER, so the link would 400 at the customer's tap.
          audience: added?.publicFillable ? 'CUSTOMER' : 'TECHNICIAN',
          instantiationOverride: null,
        },
      ];
    });
    setChecklistsPickerOpen(false);
  }

  /** The order they are listed in is the order they appear on the job. */
  function moveChecklistLink({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    setSelectedChecklistLinks((current) => {
      const ordered = [...current].sort((a, b) => a.sortOrder - b.sortOrder);
      const from = ordered.findIndex((link) => link.checklistDefinitionId === active.id);
      const to = ordered.findIndex((link) => link.checklistDefinitionId === over.id);
      if (from < 0 || to < 0) return current;
      return arrayMove(ordered, from, to).map((link, sortOrder) => ({ ...link, sortOrder }));
    });
  }

  function removeChecklistDefinition(checklistDefinitionId: string) {
    setSelectedChecklistLinks((current) =>
      current.filter((link) => link.checklistDefinitionId !== checklistDefinitionId),
    );
  }

  function updateChecklistDraft(checklistDefinitionId: string, patch: Partial<ChecklistLinkDraft>) {
    setSelectedChecklistLinks((current) =>
      current.map((link) =>
        link.checklistDefinitionId === checklistDefinitionId ? { ...link, ...patch } : link,
      ),
    );
  }

  function handleSubmit(values: JobTypeFormValues) {
    form.clearErrors('isDiagnostic');
    // Decision C — serviceCallEnabled forces one-off scheduling. Enforced
    // only when this save actually touches the conflicting inputs (or on
    // create), mirroring the backend's tolerance of pre-existing rows. The
    // SAME touched groups decide which fields the update payload serializes
    // (jobTypeFormValuesToDto updateDirtyGroups): the backend re-validates
    // any PRESENT field, so an untouched group must stay out of the PATCH or
    // a legacy enabled+recurring row 400s on unrelated edits.
    const dirtyFields = form.formState.dirtyFields;
    const serviceCallTouched =
      mode === 'create' ||
      Boolean(dirtyFields.isDiagnostic) ||
      Boolean(dirtyFields.serviceCallFeePolicy);
    const schedulingTouched =
      mode === 'create' ||
      Boolean(dirtyFields.schedulingAvailability) ||
      Boolean(dirtyFields.recurrenceFrequency) ||
      Boolean(dirtyFields.recurrenceInterval) ||
      Object.keys(dirtyFields).some(
        (field) => field.startsWith('oneOff') || field.startsWith('recurring'),
      );
    const serviceCallSchedulingTouched = serviceCallTouched || schedulingTouched;
    if (
      values.isDiagnostic &&
      values.schedulingAvailability === 'RECURRING' &&
      serviceCallSchedulingTouched
    ) {
      form.setError('isDiagnostic', {
        type: 'manual',
        message: SERVICE_CALL_ONE_OFF_MESSAGE,
      });
      blockSave('scheduling', SERVICE_CALL_ONE_OFF_MESSAGE);
      return;
    }
    if (!anyTechnician && specificSkillIds.length === 0) {
      setSkillsError(REQUIRED_SKILL_MESSAGE);
      blockSave('skills', REQUIRED_SKILL_MESSAGE);
      return;
    }
    const { businessUnitIds, ...valuesWithoutBusinessUnits } = values;
    const normalized = {
      ...valuesWithoutBusinessUnits,
      name: values.name.trim(),
      shortCode: values.shortCode.trim().toUpperCase(),
      ...(hasNoBusinessUnits ? (isEdit ? {} : { businessUnitIds: [] }) : { businessUnitIds }),
      description: values.description?.trim() ?? '',
      customerPhrases: values.customerPhrases,
    };

    // Never send the general skill alongside real ones — the backend deletes
    // that link, which is exactly the selection users watched disappear. In
    // "any technician" mode it is the only entry (and an empty list is fine
    // too: the backend links the general skill itself).
    const requiredSkills: RequiredSkillDraft[] = (
      anyTechnician ? [...selectedSkills] : specificSkillIds
    ).map((serviceTypeId) => ({
      serviceTypeId,
    }));

    // An empty list only works because the server links the general skill
    // itself — and for on-duty scheduling it refuses the request before it gets
    // that far. Keyed on what we are actually about to send: a tenant that has
    // the general skill sends its id and is fine, here or in edit.
    if (requiredSkills.length === 0 && values.onDutySchedulingEnabled) {
      setSkillsError(ON_DUTY_REQUIRES_SKILL_MESSAGE);
      blockSave('skills', ON_DUTY_REQUIRES_SKILL_MESSAGE);
      return;
    }

    if (isEdit && jobType) {
      onUpdate(jobType.id, normalized, requiredSkills, selectedChecklistLinks, lineItems, {
        serviceCall: serviceCallTouched,
        scheduling: schedulingTouched,
      });
      return;
    }

    onCreate(normalized, requiredSkills, selectedChecklistLinks, lineItems);
  }

  /**
   * Schema-level rejects never reach handleSubmit. The offending input can sit
   * in a collapsed section, where react-hook-form's focus-first-error has no
   * node to focus and the Save looks like a no-op — so say what is wrong.
   */
  function handleInvalidSubmit(errors: FieldErrors<JobTypeFormValues>) {
    const firstMessage = Object.values(errors).find(
      (error) => typeof error?.message === 'string' && error.message,
    )?.message;
    toast.error(
      typeof firstMessage === 'string'
        ? firstMessage
        : 'Some required fields are missing — check the highlighted sections.',
    );
  }

  const title = isEdit ? 'Edit Job Type' : 'Create Job Type';

  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col', isPageLayout && 'h-full min-h-0 bg-[#f4f4f1]')}
    >
      {/* Keep pricebook popovers inside the Sheet's scroll-lock boundary. */}
      <div ref={setPickerPortal} />
      <div
        className={cn(
          'border-b bg-background px-5 py-4',
          isPageLayout && 'sticky top-0 z-20 px-6 py-3',
        )}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2">
            {onBack ? (
              <Button type="button" variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
            ) : null}
            <div>
              {isPageLayout ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    Job types · {jobType?.name ?? 'Template'}
                  </p>
                  <h1 className="truncate text-base font-semibold">{jobType?.name ?? title}</h1>
                </>
              ) : (
                <>
                  <h2 className="text-base font-semibold">{title}</h2>
                  <p className="text-sm text-muted-foreground">
                    Configure work defaults, required skills, and form attachments.
                  </p>
                </>
              )}
            </div>
          </div>
          {isPageLayout ? (
            <div className="flex shrink-0 items-center gap-2">
              <span className="hidden text-xs text-muted-foreground sm:inline">
                {hasChanges ? 'Unsaved changes' : 'No changes'}
              </span>
              <Button type="button" variant="outline" onClick={onBack ?? onClose}>
                Discard
              </Button>
              <Button
                type="submit"
                form="job-type-editor-form"
                disabled={isPending || (isEdit && !jobType) || !hasChanges}
              >
                {isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <Form {...form}>
        <form
          id="job-type-editor-form"
          onSubmit={form.handleSubmit(handleSubmit, handleInvalidSubmit)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto overscroll-contain',
              isPageLayout
                ? 'mx-auto my-5 grid w-[calc(100%-1.5rem)] max-w-[1100px] auto-rows-max items-start gap-5 overflow-x-hidden bg-background px-5 sm:w-[calc(100%-3rem)] lg:grid-cols-1 lg:auto-rows-auto'
                : 'space-y-4 px-5 py-5',
            )}
          >
            <div ref={sectionsRef} className="jobtype-editor-sections space-y-0">
              {isEdit && !jobType ? (
                <p className="text-sm text-muted-foreground">Job type not found.</p>
              ) : (
                <>
                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={Palette}
                        label="Details"
                        badge="required"
                        open={openSections.required}
                        onToggle={() => toggleSection('required')}
                      />
                    }
                    sectionId="required"
                    open={openSections.required}
                    bodyClassName="space-y-4"
                  >
                    <div
                      className={cn(
                        'grid gap-4',
                        supportsOneOff
                          ? 'lg:grid-cols-[minmax(150px,1.5fr)_100px_minmax(150px,1.2fr)_minmax(130px,1fr)_minmax(130px,1fr)]'
                          : 'lg:grid-cols-[minmax(180px,1.5fr)_100px_minmax(180px,1.2fr)_minmax(160px,1fr)]',
                      )}
                    >
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="AC Repair"
                                disabled={isPending}
                                {...field}
                                onChange={(event) => {
                                  field.onChange(event);
                                  if (mode === 'create' && !shortCodeEdited) {
                                    form.setValue(
                                      'shortCode',
                                      deriveShortCodeFromName(event.target.value),
                                      { shouldValidate: true },
                                    );
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="shortCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Short code</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="ACRP"
                                disabled={isPending}
                                {...field}
                                onChange={(event) => {
                                  setShortCodeEdited(true);
                                  field.onChange(event.target.value.toUpperCase());
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="color"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Appearance</FormLabel>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={isPending}
                                  className="h-10 w-full justify-between px-2.5 font-normal"
                                >
                                  <span className="flex min-w-0 items-center gap-2">
                                    <JobTypeAppearanceTile
                                      color={selectedColor}
                                      icon={selectedIconName}
                                      size="sm"
                                      className="border-0"
                                    />
                                    {/* Icon name only — the swatch already
                                        shows the color; the raw hex read as
                                        technical noise to users. */}
                                    <span className="truncate">{selectedIconName}</span>
                                  </span>
                                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-[340px] space-y-4 p-4">
                                <div className="space-y-2">
                                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                    Color
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {PRESET_COLORS.map((color) => {
                                      const selected =
                                        selectedColor.toLowerCase() === color.toLowerCase();
                                      return (
                                        <button
                                          key={color}
                                          type="button"
                                          aria-label={`Use color ${color}`}
                                          aria-pressed={selected}
                                          className={cn(
                                            'flex h-8 w-8 items-center justify-center rounded-full border-2 border-white ring-1 ring-transparent transition-transform hover:scale-105',
                                            selected && 'ring-foreground',
                                          )}
                                          style={{ backgroundColor: color }}
                                          onClick={() => field.onChange(color)}
                                        >
                                          {selected ? (
                                            <Check className="h-4 w-4 text-[#292a2c]" />
                                          ) : null}
                                        </button>
                                      );
                                    })}
                                    <label
                                      className="relative h-8 w-8 cursor-pointer overflow-hidden rounded-full border border-border"
                                      aria-label="Choose a custom color"
                                    >
                                      <input
                                        type="color"
                                        value={field.value}
                                        onChange={(event) => field.onChange(event.target.value)}
                                        className="absolute -inset-2 h-12 w-12 cursor-pointer border-0 p-0"
                                      />
                                    </label>
                                  </div>
                                </div>
                                <div className="border-t pt-4">
                                  <FormField
                                    control={form.control}
                                    name="icon"
                                    render={({ field: iconField }) => (
                                      <FormItem>
                                        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                                          Icon
                                        </p>
                                        <div className="grid grid-cols-7 gap-2">
                                          {JOB_TYPE_ICON_OPTIONS.map(({ name, Icon }) => {
                                            const selected = iconField.value === name;
                                            return (
                                              <button
                                                key={name}
                                                type="button"
                                                title={name}
                                                aria-label={`Use icon ${name}`}
                                                aria-pressed={selected}
                                                disabled={isPending}
                                                onClick={() => iconField.onChange(name)}
                                                className={cn(
                                                  'flex h-9 w-9 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-muted',
                                                  selected &&
                                                    'border-foreground bg-muted text-foreground ring-1 ring-foreground',
                                                )}
                                              >
                                                <Icon className="h-4 w-4" />
                                              </button>
                                            );
                                          })}
                                        </div>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {supportsOneOff ? (
                        <FormField
                          control={form.control}
                          name="pipelineId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Pipeline</FormLabel>
                              <Select
                                value={field.value ?? 'none'}
                                disabled={isPending}
                                onValueChange={(value) => {
                                  field.onChange(value === 'none' ? null : value);
                                  form.setValue('defaultStageId', null, { shouldDirty: true });
                                }}
                              >
                                <FormControl>
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="No pipeline" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="none">No pipeline</SelectItem>
                                  {pipelines.map((pipeline) => (
                                    <SelectItem key={pipeline.id} value={pipeline.id}>
                                      {pipeline.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}

                      {activeBusinessUnits.length > 0 ? (
                        <FormField
                          control={form.control}
                          name="businessUnitIds"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Business units</FormLabel>
                              <FormControl>
                                <BusinessUnitMultiSelect
                                  value={field.value ?? []}
                                  disabled={isPending}
                                  onChange={field.onChange}
                                  emptyLabel="Unassigned"
                                />
                              </FormControl>
                              <FormDescription>
                                Each job picks one of these from the customer type, the address and
                                the technician; the first is the fallback.
                                {unitsSplitOnSegment(field.value ?? [], activeBusinessUnits) ? (
                                  <span className="mt-1 block font-medium text-foreground">
                                    Iris will ask whether the address is a home or a business.
                                  </span>
                                ) : null}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}
                    </div>

                    <div className="space-y-4">
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between">
                              <FormLabel>
                                Description{' '}
                                <span className="font-normal text-muted-foreground">
                                  — optional
                                </span>
                              </FormLabel>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 gap-1 px-2 text-xs text-muted-foreground"
                                title="Polish the wording with Iris (keeps the meaning and language)"
                                disabled={isPending || polishing !== null || !field.value?.trim()}
                                onClick={() => void polishField('description')}
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                {polishing === 'description' ? 'Polishing…' : 'Polish'}
                              </Button>
                            </div>
                            <FormControl>
                              <Textarea
                                placeholder="Shown to techs on the job card."
                                disabled={isPending}
                                rows={3}
                                className="min-h-[88px] resize-none"
                                {...field}
                                value={field.value ?? ''}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {supportsOneOff && selectedPipelineStages.length > 0 ? (
                        <FormField
                          control={form.control}
                          name="defaultStageId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Initial stage</FormLabel>
                              <Select
                                value={field.value ?? 'auto'}
                                disabled={isPending}
                                onValueChange={(value) =>
                                  field.onChange(value === 'auto' ? null : value)
                                }
                              >
                                <FormControl>
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="First stage" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="auto">First stage (default)</SelectItem>
                                  {selectedPipelineStages.map((stage) => (
                                    <SelectItem key={stage.id} value={stage.id}>
                                      {stage.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}
                    </div>
                  </FormAccordionCard>

                  {/* Pricing sits directly below the Service call card: the
                      visit is priced by these line items, and — while the
                      capability is on — the visit-price rule lives here too. */}
                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={ClipboardList}
                        label="Pricing & line items"
                        badge="optional"
                        open={openSections.pricing}
                        onToggle={() => toggleSection('pricing')}
                      />
                    }
                    sectionId="pricing"
                    open={openSections.pricing}
                    bodyClassName="space-y-4"
                  >
                    {liveValues.isDiagnostic ? (
                      <div className="space-y-3 rounded-md border bg-muted/20 p-3">
                        <p className="text-xs text-muted-foreground">
                          For a diagnostic service, the visit price is whatever these line items
                          total — with no line items, the visit is free. To charge for the visit,
                          add a line item with its price. Each line has its own{' '}
                          <span className="font-medium">Credit toward work</span> checkbox: checked
                          (the default), that line&apos;s price comes off what the customer pays for
                          the work when they proceed — on the estimate or on the resulting job,
                          decided on the fly. Unchecked, the line is always charged (for example,
                          materials consumed during the visit). If the customer doesn&apos;t
                          proceed, the visit price is all they pay.
                        </p>
                      </div>
                    ) : null}
                    {/* Visit deposit — lives with the pricing it is collected
                        against; available on EVERY job type, not only service
                        calls (owner: "embedded in the lines section", "useful
                        also for all type of jobs"). */}
                    <div className="space-y-3 rounded-md border bg-muted/20 p-3">
                      <FormField
                        control={form.control}
                        name="visitDepositEnabled"
                        render={({ field: depositField }) => (
                          <FormItem className="flex items-start gap-2 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={depositField.value}
                                disabled={
                                  isPending || (depositPaymentsBlocked && !depositField.value)
                                }
                                onCheckedChange={(checked) =>
                                  depositField.onChange(checked === true)
                                }
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="text-sm font-normal">
                                Require a deposit to book the visit
                              </FormLabel>
                              <p className="text-xs text-muted-foreground">
                                Iris and online booking send a secure payment link when the visit is
                                booked, so the deposit is collected before the technician goes on
                                site.
                              </p>
                            </div>
                          </FormItem>
                        )}
                      />
                      {depositPaymentsBlocked ? (
                        <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
                          {paymentSettings?.primaryProcessor &&
                          paymentSettings.primaryProcessor !== 'stripe'
                            ? liveValues.visitDepositEnabled
                              ? 'Visit-deposit payment links are sent through Stripe, and this account uses a different primary processor — links cannot be sent, so the office will need to collect this deposit manually.'
                              : 'Visit-deposit payment links are sent through Stripe. Switch the primary processor to Stripe in Settings → Payments to enable this option.'
                            : liveValues.visitDepositEnabled
                              ? 'Deposit links cannot be sent because no payment processor is connected. Configure payments in Settings → Payments, or the office will need to collect this deposit manually.'
                              : 'Visit deposits need a connected payment processor. Configure payments in Settings → Payments to enable this option.'}
                        </p>
                      ) : null}
                      {liveValues.visitDepositEnabled ? (
                        <div className="flex items-start gap-2 pl-6">
                          <FormField
                            control={form.control}
                            name="visitDepositValue"
                            render={({ field: valueField }) => (
                              <FormItem className="w-32 space-y-1">
                                <FormLabel className="text-xs">Deposit amount</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    disabled={isPending}
                                    {...valueField}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="visitDepositType"
                            render={({ field: typeField }) => (
                              <FormItem className="w-44 space-y-1">
                                <FormLabel className="text-xs">Type</FormLabel>
                                <Select
                                  value={typeField.value}
                                  onValueChange={typeField.onChange}
                                  disabled={isPending}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="FLAT">$ fixed amount</SelectItem>
                                    <SelectItem value="PERCENT">% of visit price</SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                        </div>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Line items</p>
                      <EstimateLineItemsSection
                        open
                        bare
                        onToggle={() => undefined}
                        emptyStateEntityLabel="job type"
                        enableLineAttachments={false}
                        estimateId={undefined}
                        pricingMode="STANDARD"
                        picker={
                          <LineItemPicker
                            onAdd={addPickedLineItem}
                            excludeIds={lineItems.map((li) => li.lineItemId)}
                            portalContainer={pickerPortal}
                          />
                        }
                        lineItems={lineItems}
                        lineItemPreviews={lineItemPreviews}
                        updateQuantity={updateQuantity}
                        updateName={updateName}
                        updateType={updateType}
                        updateUnit={updateUnit}
                        updateBasePrice={updateBasePrice}
                        updatePriceStrategy={updatePriceStrategy}
                        updateLaborRate={updateLaborRate}
                        updateEstimatedHours={updateEstimatedHours}
                        updateDescription={updateDescription}
                        updateMarkupPercent={updateMarkupPercent}
                        updateCostBasis={updateCostBasis}
                        updateTaxable={updateTaxable}
                        showServiceCallCredit={liveValues.isDiagnostic}
                        updateServiceCallCredit={updateServiceCallCredit}
                        toggleOptional={toggleOptional}
                        toggleSelected={toggleSelected}
                        removeLineItem={removeLineItem}
                        onReorder={reorderLineItems}
                        membershipEligibleRowIds={membershipEligibleRowIds}
                        membershipInfo={null}
                      />
                    </div>
                  </FormAccordionCard>

                  {/* SPEC §4.1 (binary visibility) — the header switch IS the
                      Iris visibility control: ON = LISTED (Iris knows this
                      service exists), OFF = NOT_LISTED (Iris has no idea it
                      exists). Toggling drives the section's expansion; the
                      config below stays stored either way, so deactivating is
                      fully reversible. */}
                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={Bot}
                        label="Iris"
                        badge="customer-facing"
                        open={openSections.iris}
                        onToggle={() => toggleSection('iris')}
                        extra={
                          <FormField
                            control={form.control}
                            name="irisActivated"
                            render={({ field }) => (
                              <div className="flex flex-col items-end gap-0.5">
                                <label className="flex cursor-pointer items-center gap-2 text-sm font-normal">
                                  <span>Enable for Iris</span>
                                  <Switch
                                    aria-label="Enable for Iris"
                                    checked={field.value}
                                    disabled={isPending}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked);
                                      // The switch drives the section: activating
                                      // reveals the options, deactivating tucks
                                      // them away (values are preserved).
                                      setOpenSections((current) => ({
                                        ...current,
                                        iris: checked,
                                      }));
                                      // Auto-suggest: flipping Iris ON with an
                                      // empty config drafts one from the name
                                      // (never overwrites typed content).
                                      if (checked && isIrisSuggestTargetEmpty(form.getValues())) {
                                        void runIrisSuggestions(false);
                                      }
                                    }}
                                  />
                                </label>
                                {/* Always-visible legend: the listing outcome
                                    at a glance, doubling as the suggestion
                                    status line while Iris is engaged. */}
                                <p className="text-[11px] text-muted-foreground">
                                  {suggestStatus === 'pending'
                                    ? 'Suggesting a configuration…'
                                    : field.value && !liveValues.name.trim()
                                      ? 'Name the job type first — suggestions are based on it.'
                                      : suggestStatus === 'error'
                                        ? 'Suggestions are unavailable right now — try again.'
                                        : field.value
                                          ? 'Iris is listing this job type'
                                          : 'Iris will not list this job type'}
                                </p>
                              </div>
                            )}
                          />
                        }
                      />
                    }
                    sectionId="iris"
                    open={openSections.iris}
                    bodyClassName="space-y-5"
                  >
                    <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
                      {liveValues.irisActivated
                        ? 'Iris knows this service exists and can offer, match, and book it. These settings control how it recognizes and explains the service; they do not replace the operational description, pricing, or scheduling rules.'
                        : "Iris is deactivated for this job type — it doesn't know this service exists and will never offer, match, or book it. Your configuration below is preserved; activate Iris to edit it. Dashboard and manual use are unaffected."}
                    </div>

                    <FormField
                      control={form.control}
                      name="isDiagnostic"
                      render={({ field }) => (
                        <FormItem className="rounded-md border bg-card p-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="space-y-1">
                              <FormLabel>Diagnostic service</FormLabel>
                              <p className="text-xs text-muted-foreground">
                                The technician evaluates the issue on site and determines the final
                                work scope there. Iris presents the configured line-item total as
                                the evaluation visit price.
                              </p>
                            </div>
                            <FormControl>
                              <Switch
                                aria-label="Diagnostic service"
                                checked={field.value}
                                disabled={isPending}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Force-regenerate: unlike the auto-fire on activation,
                        the button may overwrite non-empty fields. */}
                    {liveValues.irisActivated && (
                      <div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={
                            isPending || suggestStatus === 'pending' || !liveValues.name.trim()
                          }
                          onClick={() => void runIrisSuggestions(true)}
                        >
                          <Sparkles className="mr-1 h-4 w-4" />
                          Suggest configuration
                        </Button>
                      </div>
                    )}

                    {/* Service-call interplay (mirrors the operations strict
                        gating): an Iris booking materialises as a service call
                        exactly when the type's Service call capability is on —
                        otherwise as a regular job. */}
                    {liveValues.irisActivated && (
                      <p className="text-xs text-muted-foreground">
                        {liveValues.isDiagnostic
                          ? 'Iris presents this as an evaluation visit where the technician determines the final work scope on site.'
                          : 'Iris presents this as a defined service whose work scope is already known.'}
                      </p>
                    )}

                    <div className="grid items-start gap-4 lg:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="irisCustomerDescription"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between">
                              <FormLabel>Customer-facing description</FormLabel>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 gap-1 px-2 text-xs text-muted-foreground"
                                title="Polish the wording with Iris (keeps the meaning and language)"
                                disabled={
                                  isPending ||
                                  irisConfigDisabled ||
                                  polishing !== null ||
                                  !field.value?.trim()
                                }
                                onClick={() => void polishField('irisCustomerDescription')}
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                {polishing === 'irisCustomerDescription' ? 'Polishing…' : 'Polish'}
                              </Button>
                            </div>
                            <FormControl>
                              <Textarea
                                placeholder="Explain what this service does in plain customer language."
                                rows={4}
                                disabled={isPending || irisConfigDisabled}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="customerPhrases"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Match when customers say</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={'no hot water\nwater heater leaking\nhot-water tank'}
                                rows={4}
                                disabled={isPending || irisConfigDisabled}
                                {...field}
                              />
                            </FormControl>
                            <p className="text-xs text-muted-foreground">
                              One example per line. Add equipment, symptoms, and common wording.
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="irisMatchingExclusions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Do not match when customers say</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={'whole-house boiler heating\nfrozen water main'}
                              rows={3}
                              disabled={isPending || irisConfigDisabled}
                              {...field}
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">
                            Strong negative examples prevent this Job Type from being selected. One
                            per line.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid items-start gap-4 lg:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="irisIncludedWork"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Service includes</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={'Diagnostic inspection\nStandard repair labor'}
                                rows={4}
                                disabled={isPending || irisConfigDisabled}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="irisExcludedWork"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Service does not include</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder={'Permit fees\nElectrical panel replacement'}
                                rows={4}
                                disabled={isPending || irisConfigDisabled}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="irisFaqs"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Common questions and answers</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={
                                'Do you bring parts? | The technician carries common repair parts.\nHow long does it take? | Most visits fit the configured appointment duration.'
                              }
                              rows={5}
                              disabled={isPending || irisConfigDisabled}
                              {...field}
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground">
                            One per line using Question | Answer. Iris uses these only after the Job
                            Type is matched.
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid items-start gap-4 lg:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="irisWarranty"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Warranty</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Explain the warranty Iris may quote to customers."
                                rows={4}
                                disabled={isPending || irisConfigDisabled}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="irisAdditionalGuidance"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Additional Iris guidance</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Optional service-specific instructions for unusual cases."
                                rows={4}
                                disabled={isPending || irisConfigDisabled}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {/* Decision D — intake survey. Simple free-text rows with
                        stable ids; Iris asks them during intake and the
                        answers are saved onto the created job. */}
                    <FormField
                      control={form.control}
                      name="irisSurveyQuestions"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Survey questions</FormLabel>
                          <p className="text-xs text-muted-foreground">
                            Iris asks these one at a time while taking the booking and saves the
                            answers onto the created job. Photo questions ask the customer to text a
                            picture, saved on the job as an attachment. Up to{' '}
                            {MAX_IRIS_SURVEY_QUESTIONS} questions.
                          </p>
                          <FormField
                            control={form.control}
                            name="irisSurveyTiming"
                            render={({ field: timingField }) => (
                              <FormItem className="flex items-center gap-2 space-y-0">
                                <FormLabel className="text-xs font-normal text-muted-foreground">
                                  Ask the questions
                                </FormLabel>
                                <Select
                                  value={timingField.value}
                                  onValueChange={timingField.onChange}
                                  disabled={isPending || irisConfigDisabled}
                                >
                                  <FormControl>
                                    <SelectTrigger className="h-8 w-64 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="BEFORE_SLOTS">
                                      Before offering time slots (default)
                                    </SelectItem>
                                    <SelectItem value="AFTER_SLOT">
                                      After the customer picks a slot
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormItem>
                            )}
                          />
                          <div className="space-y-2">
                            {field.value.map((row, index) => (
                              <div key={row.id} className="flex items-center gap-1.5">
                                <FormControl>
                                  <Input
                                    aria-label={`Survey question ${index + 1}`}
                                    placeholder="e.g. Is the unit under warranty?"
                                    disabled={isPending || irisConfigDisabled}
                                    value={row.question}
                                    onChange={(event) =>
                                      field.onChange(
                                        field.value.map((entry) =>
                                          entry.id === row.id
                                            ? { ...entry, question: event.target.value }
                                            : entry,
                                        ),
                                      )
                                    }
                                  />
                                </FormControl>
                                <Select
                                  value={row.type ?? 'TEXT'}
                                  onValueChange={(value) =>
                                    field.onChange(
                                      field.value.map((entry) =>
                                        entry.id === row.id
                                          ? { ...entry, type: value as 'TEXT' | 'PHOTO' }
                                          : entry,
                                      ),
                                    )
                                  }
                                  disabled={isPending || irisConfigDisabled}
                                >
                                  <SelectTrigger
                                    className="h-9 w-24 shrink-0 text-xs"
                                    aria-label={`Survey question ${index + 1} answer type`}
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="TEXT">Text</SelectItem>
                                    <SelectItem value="PHOTO">Photo</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Move survey question ${index + 1} up`}
                                  disabled={isPending || irisConfigDisabled || index === 0}
                                  onClick={() => {
                                    const next = [...field.value];
                                    [next[index - 1], next[index]] = [
                                      next[index]!,
                                      next[index - 1]!,
                                    ];
                                    field.onChange(next);
                                  }}
                                >
                                  <ArrowUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Move survey question ${index + 1} down`}
                                  disabled={
                                    isPending ||
                                    irisConfigDisabled ||
                                    index === field.value.length - 1
                                  }
                                  onClick={() => {
                                    const next = [...field.value];
                                    [next[index], next[index + 1]] = [
                                      next[index + 1]!,
                                      next[index]!,
                                    ];
                                    field.onChange(next);
                                  }}
                                >
                                  <ArrowDown className="h-4 w-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label={`Remove survey question ${index + 1}`}
                                  disabled={isPending || irisConfigDisabled}
                                  onClick={() =>
                                    field.onChange(
                                      field.value.filter((entry) => entry.id !== row.id),
                                    )
                                  }
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={
                              isPending ||
                              irisConfigDisabled ||
                              field.value.length >= MAX_IRIS_SURVEY_QUESTIONS
                            }
                            onClick={() =>
                              field.onChange([
                                ...field.value,
                                {
                                  id: newIrisSurveyQuestionId(),
                                  question: '',
                                  type: 'TEXT' as const,
                                },
                              ])
                            }
                          >
                            <Plus className="mr-1 h-4 w-4" />
                            Add question
                          </Button>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Decision E — equipment awareness. */}
                    <FormField
                      control={form.control}
                      name="irisAskEquipment"
                      render={({ field }) => (
                        <FormItem className="space-y-2 rounded-md border p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="space-y-1">
                              <FormLabel>Ask about existing equipment</FormLabel>
                              <p className="text-xs text-muted-foreground">
                                When the customer has equipment on file, Iris asks whether this
                                visit concerns one of them and links it to the job.
                              </p>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                disabled={isPending || irisConfigDisabled}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </FormAccordionCard>

                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={Wrench}
                        label="Scheduling"
                        badge="optional"
                        open={openSections.scheduling}
                        onToggle={() => toggleSection('scheduling')}
                      />
                    }
                    sectionId="scheduling"
                    open={openSections.scheduling}
                    bodyClassName="space-y-4"
                  >
                    <FormField
                      control={form.control}
                      name="onDutySchedulingEnabled"
                      render={({ field }) => (
                        <FormItem className="space-y-2 rounded-md border p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="space-y-1">
                              <FormLabel>On-duty emergency scheduling</FormLabel>
                              <p className="text-xs text-muted-foreground">
                                Emergency requests may use a qualified technician's active on-duty
                                shift. At least one required skill must be configured.
                              </p>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                disabled={isPending}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    {purchasingEnabled ? (
                      <FormField
                        control={form.control}
                        name="requiresParts"
                        render={({ field }) => (
                          <FormItem className="space-y-2 rounded-md border p-3">
                            <div className="flex items-center justify-between gap-4">
                              <div className="space-y-1">
                                <FormLabel>Needs its parts before a visit</FormLabel>
                                <p className="text-xs text-muted-foreground">
                                  Scheduling a visit before every part for the job is in hand warns,
                                  or is refused, as your purchasing settings say.
                                </p>
                              </div>
                              <FormControl>
                                <Switch
                                  aria-label="Needs its parts before a visit"
                                  checked={field.value}
                                  disabled={isPending}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ) : null}
                    <div className="grid gap-x-4 gap-y-3 md:grid-cols-6 xl:grid-cols-12">
                      <FormField
                        control={form.control}
                        name="defaultDuration"
                        render={({ field }) => (
                          <FormItem className="md:col-span-2 xl:col-span-2">
                            <FormLabel>Default duration</FormLabel>
                            <div className="relative">
                              <FormControl>
                                <Input
                                  className="pr-12"
                                  type="number"
                                  min={1}
                                  max={1440}
                                  disabled={isPending}
                                  {...field}
                                />
                              </FormControl>
                              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                                min
                              </span>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="minCrewSize"
                        render={({ field }) => (
                          <FormItem className="md:order-2 md:col-span-2 xl:col-span-1">
                            <FormLabel>Min crew</FormLabel>
                            <div className="grid min-h-9 grid-cols-[2.5rem_1fr_2.5rem] overflow-hidden rounded-md border md:hidden">
                              <button
                                type="button"
                                className="text-muted-foreground hover:bg-muted disabled:opacity-50"
                                disabled={isPending || Number(field.value) <= 1}
                                aria-label="Decrease minimum crew"
                                onClick={() => field.onChange(Math.max(1, Number(field.value) - 1))}
                              >
                                −
                              </button>
                              <FormControl>
                                <Input
                                  className="h-auto rounded-none border-y-0 text-center shadow-none focus-visible:ring-0"
                                  type="number"
                                  min={1}
                                  max={50}
                                  disabled={isPending}
                                  {...field}
                                />
                              </FormControl>
                              <button
                                type="button"
                                className="text-muted-foreground hover:bg-muted disabled:opacity-50"
                                disabled={isPending || Number(field.value) >= 50}
                                aria-label="Increase minimum crew"
                                onClick={() =>
                                  field.onChange(Math.min(50, Number(field.value) + 1))
                                }
                              >
                                +
                              </button>
                            </div>
                            <FormControl>
                              <Input
                                className="hidden md:block"
                                type="number"
                                min={1}
                                max={50}
                                disabled={isPending}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="defaultPriority"
                        render={({ field }) => (
                          <FormItem className="md:order-4 md:col-span-3 xl:col-span-4">
                            <FormLabel>Default priority</FormLabel>
                            <SegmentedControl
                              value={field.value}
                              disabled={isPending}
                              options={PRIORITY_OPTIONS}
                              onChange={field.onChange}
                              className="!grid w-full grid-cols-4 [&>button]:w-full"
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="schedulingAvailability"
                        render={({ field }) => (
                          <FormItem className="md:order-5 md:col-span-3 xl:col-span-2">
                            <FormLabel>Job type</FormLabel>
                            <SegmentedControl
                              value={field.value}
                              // Decision C — a service-call type is always
                              // one-off, so the control locks while the
                              // capability is on. A legacy recurring row with
                              // the capability on keeps the control live: the
                              // only way out is switching it to one-off.
                              disabled={
                                isPending || (liveValues.isDiagnostic && field.value === 'ONE_OFF')
                              }
                              options={[
                                { value: 'ONE_OFF', label: 'One-off' },
                                { value: 'RECURRING', label: 'Recurring' },
                              ]}
                              onChange={field.onChange}
                              className="!grid w-full grid-cols-2 [&>button]:w-full"
                            />
                            {liveValues.isDiagnostic ? (
                              <p className="text-xs text-muted-foreground">
                                Diagnostic services are always one-off visits
                              </p>
                            ) : null}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    {supportsOneOff ? (
                      <div className="space-y-4 border-t pt-5">
                        <FormField
                          control={form.control}
                          name="oneOffPatternMode"
                          render={({ field }) => (
                            <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                              <FormLabel>Schedule setup</FormLabel>
                              <SegmentedControl
                                className="!grid w-full grid-cols-1 sm:grid-cols-2 [&>button]:w-full"
                                value={field.value}
                                disabled={isPending}
                                options={[
                                  { value: 'CHOOSE_ON_CREATE', label: 'Decide on each job' },
                                  { value: 'PRESET', label: 'Pre-fill these defaults' },
                                ]}
                                onChange={field.onChange}
                              />
                            </FormItem>
                          )}
                        />
                        {oneOffPatternMode === 'PRESET' ? (
                          <SchedulingBranch>
                            <FormField
                              control={form.control}
                              name="oneOffScheduleMode"
                              render={({ field }) => (
                                <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                  <FormLabel>Then</FormLabel>
                                  <SegmentedControl
                                    className="w-full [&>button]:flex-1"
                                    value={field.value}
                                    options={[
                                      { value: 'SCHEDULE_NOW', label: 'Schedule now' },
                                      { value: 'SCHEDULE_LATER', label: 'Schedule later' },
                                      { value: 'NO_VISIT', label: 'No visit yet' },
                                    ]}
                                    onChange={field.onChange}
                                  />
                                </FormItem>
                              )}
                            />
                            {oneOffScheduleMode === 'SCHEDULE_NOW' ? (
                              <FormField
                                control={form.control}
                                name="oneOffVisitShape"
                                render={({ field }) => (
                                  <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                    <FormLabel className="flex items-center gap-2">
                                      <span className="mr-1 text-muted-foreground">↳</span>
                                      Visit type
                                    </FormLabel>
                                    <SegmentedControl
                                      className="w-full [&>button]:flex-1"
                                      value={field.value}
                                      disabled={isPending}
                                      options={[
                                        { value: 'TIMED', label: 'Timed' },
                                        { value: 'ALL_DAY', label: 'All day' },
                                        { value: 'FULL_DAY', label: 'Full day' },
                                        { value: 'MULTI_DAY', label: 'Multi-day' },
                                      ]}
                                      onChange={field.onChange}
                                    />
                                  </FormItem>
                                )}
                              />
                            ) : null}
                            {oneOffScheduleMode === 'SCHEDULE_NOW' &&
                            oneOffVisitShape === 'TIMED' ? (
                              <FormField
                                control={form.control}
                                name="oneOffPreferredTimeWindow"
                                render={({ field }) => (
                                  <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                    <FormLabel className="flex items-center gap-2 pl-5">
                                      <span className="mr-1 text-muted-foreground">↳</span>
                                      Time preference
                                    </FormLabel>
                                    <SegmentedControl
                                      className="w-full [&>button]:flex-1"
                                      value={field.value}
                                      disabled={isPending}
                                      options={[
                                        { value: 'ANY', label: 'Any time' },
                                        { value: 'MORNING', label: 'Morning' },
                                        { value: 'AFTERNOON', label: 'Afternoon' },
                                        { value: 'SPECIFIC', label: 'A set time' },
                                      ]}
                                      onChange={field.onChange}
                                    />
                                  </FormItem>
                                )}
                              />
                            ) : null}
                            {oneOffScheduleMode === 'SCHEDULE_NOW' &&
                            oneOffVisitShape === 'TIMED' &&
                            oneOffPreferredTimeWindow === 'SPECIFIC' ? (
                              <FormField
                                control={form.control}
                                name="oneOffSpecificStartTime"
                                render={({ field }) => (
                                  <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                    <FormLabel className="flex items-center gap-2 pl-5">
                                      <span className="mr-1 text-muted-foreground">↳</span>
                                      Preferred start
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        className="w-full"
                                        type="time"
                                        disabled={isPending}
                                        value={field.value ?? ''}
                                        onChange={field.onChange}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            ) : null}
                            {oneOffScheduleMode === 'SCHEDULE_NOW' &&
                            oneOffVisitShape === 'MULTI_DAY' ? (
                              <FormField
                                control={form.control}
                                name="oneOffConsecutiveDays"
                                render={({ field }) => (
                                  <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                    <FormLabel className="flex items-center gap-2">
                                      <span className="mr-1 text-muted-foreground">↳</span>
                                      Consecutive days
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        className="w-full"
                                        type="number"
                                        min={2}
                                        max={30}
                                        disabled={isPending}
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            ) : null}
                            {oneOffScheduleMode === 'SCHEDULE_NOW' ? (
                              <FormField
                                control={form.control}
                                name="oneOffUrgency"
                                render={({ field }) => (
                                  <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                    <FormLabel className="flex items-center gap-2 whitespace-nowrap">
                                      <span className="mr-1 text-muted-foreground">↳</span>
                                      Booking urgency
                                    </FormLabel>
                                    <div className="flex min-h-9 w-full items-stretch rounded-md border bg-muted/35 p-0.5">
                                      {[
                                        { value: 'STANDARD' as const, label: 'Standard' },
                                        { value: 'URGENT' as const, label: 'Urgent' },
                                        { value: 'EMERGENCY' as const, label: 'Emergency' },
                                      ].map((option) => (
                                        <button
                                          key={option.value}
                                          type="button"
                                          disabled={isPending}
                                          className={cn(
                                            'flex-1 rounded-[4px] px-3 py-1.5 text-sm text-muted-foreground transition-colors',
                                            field.value === option.value &&
                                              'bg-background font-medium text-foreground shadow-sm',
                                          )}
                                          onClick={() => field.onChange(option.value)}
                                        >
                                          {option.label}
                                        </button>
                                      ))}
                                    </div>
                                  </FormItem>
                                )}
                              />
                            ) : null}
                            <p className="text-xs text-muted-foreground sm:ml-[192px]">
                              {oneOffScheduleMode === 'NO_VISIT'
                                ? 'New jobs open without a visit.'
                                : oneOffScheduleMode === 'SCHEDULE_LATER'
                                  ? 'New jobs keep their first visit for later scheduling.'
                                  : /* Built from the shared label, not the raw
                                       enum — lower-casing the union produced
                                       "a all day visit" and "a multi day
                                       visit". */
                                    `New jobs open ready to search for a ${visitShapeDisplayLabel(
                                      oneOffVisitShape,
                                    ).toLowerCase()} visit.`}
                            </p>
                          </SchedulingBranch>
                        ) : null}
                        {oneOffPatternMode === 'CHOOSE_ON_CREATE' ? (
                          <p className="text-xs text-muted-foreground sm:ml-36">
                            Nothing is pre-filled. The dispatcher chooses how to schedule each new
                            one-off job.
                          </p>
                        ) : null}
                      </div>
                    ) : null}

                    {supportsRecurring ? (
                      <>
                        {editingRecurringRule ? (
                          <div className="space-y-4 border-t pt-5" aria-hidden="true">
                            <div className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                              <p className="text-sm font-medium">Schedule pattern</p>
                              <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                <SegmentedControl
                                  className="!grid w-full min-w-0 flex-1 grid-cols-2 [&>button]:w-full"
                                  value="USE_RULE"
                                  options={[
                                    {
                                      value: 'CHOOSE_ON_CREATE',
                                      label: 'Decide on each job',
                                    },
                                    { value: 'USE_RULE', label: 'Use a rule' },
                                  ]}
                                  onChange={() => undefined}
                                />
                                <Button
                                  className="w-full shrink-0 sm:w-auto"
                                  type="button"
                                  variant="outline"
                                  tabIndex={-1}
                                >
                                  Edit rule
                                </Button>
                              </div>
                            </div>
                            {recurringRuleSummary ? (
                              <div className="grid gap-3 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                <div className="hidden sm:block" aria-hidden="true" />
                                <div className="min-w-0">
                                  <p className="min-w-0 text-sm">
                                    <span className="font-medium">
                                      {recurringRuleSummary.title}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {' · '}
                                      {recurringRuleSummary.detail}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {editingRecurringRule ? (
                          <button
                            type="button"
                            aria-label="Cancel recurrence rule editing"
                            className={cn(
                              'fixed inset-0 z-[69] cursor-default bg-black/20 transition-opacity duration-200',
                              recurringRuleDrawerVisible ? 'opacity-100' : 'opacity-0',
                            )}
                            onClick={() => closeRecurringRuleEditor(true)}
                          />
                        ) : null}
                        <div
                          role={editingRecurringRule ? 'dialog' : undefined}
                          aria-modal={editingRecurringRule ? true : undefined}
                          aria-label={editingRecurringRule ? 'Recurrence rule' : undefined}
                          className={cn(
                            'space-y-4 border-t pt-5',
                            editingRecurringRule &&
                              'fixed inset-y-0 right-0 z-[70] m-0 w-full max-w-3xl overflow-y-auto border-0 bg-background p-0 shadow-2xl transition-transform duration-200 ease-out',
                            editingRecurringRule &&
                              (recurringRuleDrawerVisible ? 'translate-x-0' : 'translate-x-full'),
                          )}
                        >
                          {editingRecurringRule ? (
                            <div className="sticky top-0 z-10 border-b bg-background">
                              <div className="flex min-h-16 items-center justify-between gap-4 px-6">
                                <div className="flex min-w-0 items-center gap-3">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    aria-label="Cancel recurrence rule editing"
                                    onClick={() => closeRecurringRuleEditor(true)}
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </Button>
                                  <div className="min-w-0">
                                    <p className="font-semibold">Recurrence rule</p>
                                    <p className="truncate text-xs text-muted-foreground">
                                      {form.getValues('name') || 'New job type'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => closeRecurringRuleEditor(true)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button type="button" onClick={saveRecurringRule}>
                                    Save rule
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className={cn(editingRecurringRule && 'space-y-5 px-6 py-6')}>
                            <FormField
                              control={form.control}
                              name="recurringPatternMode"
                              render={({ field }) => (
                                <FormItem className="grid gap-2 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                  <FormLabel>Schedule pattern</FormLabel>
                                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                                    <SegmentedControl
                                      className="!grid w-full min-w-0 flex-1 grid-cols-2 [&>button]:w-full"
                                      value={
                                        editingRecurringRule
                                          ? field.value
                                          : field.value === 'CHOOSE_ON_CREATE'
                                            ? 'CHOOSE_ON_CREATE'
                                            : 'USE_RULE'
                                      }
                                      disabled={isPending}
                                      options={
                                        editingRecurringRule
                                          ? [
                                              { value: 'FIXED', label: 'Fixed days' },
                                              { value: 'FLEXIBLE', label: 'Flexible' },
                                            ]
                                          : [
                                              {
                                                value: 'CHOOSE_ON_CREATE',
                                                label: 'Decide on each job',
                                              },
                                              { value: 'USE_RULE', label: 'Use a rule' },
                                            ]
                                      }
                                      onChange={(value) => {
                                        if (value === 'USE_RULE') {
                                          field.onChange(
                                            field.value === 'CHOOSE_ON_CREATE'
                                              ? 'FIXED'
                                              : field.value,
                                          );
                                          return;
                                        }
                                        field.onChange(value);
                                      }}
                                    />
                                    {!editingRecurringRule && recurringRuleSummary ? (
                                      <Button
                                        className="w-full shrink-0 sm:w-auto"
                                        type="button"
                                        variant="outline"
                                        onClick={openRecurringRuleEditor}
                                      >
                                        Edit rule
                                      </Button>
                                    ) : null}
                                  </div>
                                </FormItem>
                              )}
                            />

                            {!editingRecurringRule && recurringRuleSummary ? (
                              <div className="grid gap-3 sm:grid-cols-[176px_minmax(0,1fr)] sm:items-center sm:gap-4">
                                <div className="hidden sm:block" aria-hidden="true" />
                                <div className="min-w-0">
                                  <p className="min-w-0 text-sm">
                                    <span className="font-medium">
                                      {recurringRuleSummary.title}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {' · '}
                                      {recurringRuleSummary.detail}
                                    </span>
                                  </p>
                                </div>
                              </div>
                            ) : null}

                            {editingRecurringRule && recurringPatternMode === 'FLEXIBLE' ? (
                              <SchedulingBranch className="space-y-4">
                                <div className="grid gap-4 sm:grid-cols-3">
                                  <FormField
                                    control={form.control}
                                    name="recurringVisitsPerCycle"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>How often</FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            min={1}
                                            max={14}
                                            disabled={isPending}
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name="recurrenceFrequency"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Per</FormLabel>
                                        <SegmentedControl
                                          className="!grid w-full grid-cols-4 [&>button]:w-full [&>button]:px-1.5"
                                          value={field.value}
                                          disabled={isPending}
                                          options={[
                                            { value: 'DAY', label: 'Day' },
                                            { value: 'WEEK', label: 'Week' },
                                            { value: 'MONTH', label: 'Month' },
                                            { value: 'YEAR', label: 'Year' },
                                          ]}
                                          onChange={field.onChange}
                                        />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name="recurrenceInterval"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Every cycle</FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            min={1}
                                            disabled={isPending}
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                                <FormField
                                  control={form.control}
                                  name="recurringDayConstraintMode"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Allowed days</FormLabel>
                                      <SegmentedControl
                                        value={field.value}
                                        disabled={isPending}
                                        options={[
                                          { value: 'ANY', label: 'Any working day' },
                                          { value: 'INCLUDE', label: 'Only these' },
                                          { value: 'EXCLUDE', label: 'Any except' },
                                        ]}
                                        onChange={(value) => {
                                          field.onChange(value);
                                          if (value === 'ANY') {
                                            form.setValue('recurringAllowedWeekDays', [], {
                                              shouldDirty: true,
                                              shouldValidate: true,
                                            });
                                          }
                                        }}
                                      />
                                    </FormItem>
                                  )}
                                />
                                {recurringDayConstraintMode !== 'ANY' ? (
                                  <FormField
                                    control={form.control}
                                    name="recurringAllowedWeekDays"
                                    render={() => (
                                      <FormItem>
                                        <div className="flex flex-wrap gap-2">
                                          {WEEKDAY_OPTIONS.map((day) => {
                                            const selected = recurringAllowedWeekDays.includes(
                                              day.value,
                                            );
                                            return (
                                              <Button
                                                key={day.value}
                                                type="button"
                                                size="sm"
                                                variant={selected ? 'default' : 'outline'}
                                                onClick={() =>
                                                  form.setValue(
                                                    'recurringAllowedWeekDays',
                                                    selected
                                                      ? recurringAllowedWeekDays.filter(
                                                          (value) => value !== day.value,
                                                        )
                                                      : [
                                                          ...recurringAllowedWeekDays,
                                                          day.value,
                                                        ].sort(),
                                                    {
                                                      shouldDirty: true,
                                                      shouldValidate: true,
                                                    },
                                                  )
                                                }
                                              >
                                                {day.label}
                                              </Button>
                                            );
                                          })}
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                          {recurringDayConstraintMode === 'INCLUDE'
                                            ? 'Routing will use only these weekdays.'
                                            : 'Routing can use any open weekday except these.'}
                                        </p>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    Routing can choose any day allowed by company scheduling.
                                  </p>
                                )}
                                <FormField
                                  control={form.control}
                                  name="recurringPlacement"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>Visit placement</FormLabel>
                                      <SegmentedControl
                                        value={field.value}
                                        disabled={isPending}
                                        options={[
                                          { value: 'SCHEDULE_NOW', label: 'Schedule now' },
                                          { value: 'ALL_DAY', label: 'All day' },
                                          { value: 'ROUTE_BASED', label: 'Route-based' },
                                        ]}
                                        onChange={field.onChange}
                                      />
                                    </FormItem>
                                  )}
                                />
                                {recurringPlacement !== 'ROUTE_BASED' ? (
                                  <div className="grid gap-4 lg:grid-cols-2">
                                    {recurringPlacement === 'SCHEDULE_NOW' ? (
                                      <FormField
                                        control={form.control}
                                        name="recurringTimeMode"
                                        render={({ field }) => (
                                          <FormItem>
                                            <FormLabel>Visit time</FormLabel>
                                            <SegmentedControl
                                              value={field.value}
                                              disabled={isPending}
                                              options={[
                                                { value: 'SAME', label: 'Same every visit' },
                                                { value: 'PER_VISIT', label: 'Set per visit' },
                                              ]}
                                              onChange={field.onChange}
                                            />
                                          </FormItem>
                                        )}
                                      />
                                    ) : null}
                                    <FormField
                                      control={form.control}
                                      name="recurringTechnicianMode"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Technician</FormLabel>
                                          <SegmentedControl
                                            value={field.value}
                                            disabled={isPending}
                                            options={[
                                              { value: 'SAME', label: 'Same best tech' },
                                              { value: 'PER_VISIT', label: 'Best tech per visit' },
                                            ]}
                                            onChange={field.onChange}
                                          />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                ) : null}
                                {recurringPlacement === 'SCHEDULE_NOW' &&
                                recurringTimeMode === 'SAME' ? (
                                  <div className="grid max-w-md gap-4 sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name="recurringVisitStartTime"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Visit start</FormLabel>
                                          <FormControl>
                                            <Input
                                              type="time"
                                              disabled={isPending}
                                              value={field.value ?? ''}
                                              onChange={field.onChange}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name="recurringVisitEndTime"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Visit end</FormLabel>
                                          <FormControl>
                                            <Input
                                              type="time"
                                              disabled={isPending}
                                              value={field.value ?? ''}
                                              onChange={field.onChange}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                ) : null}
                              </SchedulingBranch>
                            ) : null}

                            {editingRecurringRule && recurringPatternMode === 'FIXED' ? (
                              <SchedulingBranch>
                                <div className="grid gap-4 lg:grid-cols-2">
                                  <FormField
                                    control={form.control}
                                    name="recurringPlacement"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Visit placement</FormLabel>
                                        <SegmentedControl
                                          value={field.value}
                                          disabled={isPending}
                                          options={[
                                            { value: 'SCHEDULE_NOW', label: 'Schedule now' },
                                            { value: 'ALL_DAY', label: 'All day' },
                                            ...(recurrenceFrequency === 'WEEK'
                                              ? [
                                                  {
                                                    value: 'ROUTE_BASED' as const,
                                                    label: 'Route-based',
                                                  },
                                                ]
                                              : []),
                                          ]}
                                          onChange={field.onChange}
                                        />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name="recurrenceFrequency"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Repeats</FormLabel>
                                        <SegmentedControl
                                          value={field.value}
                                          disabled={isPending}
                                          options={[
                                            { value: 'DAY', label: 'Daily' },
                                            { value: 'WEEK', label: 'Weekly' },
                                            { value: 'MONTH', label: 'Monthly' },
                                            { value: 'YEAR', label: 'Yearly' },
                                          ]}
                                          onChange={field.onChange}
                                        />
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name="recurrenceInterval"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Interval</FormLabel>
                                        <FormControl>
                                          <Input
                                            type="number"
                                            min={1}
                                            disabled={isPending}
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  {recurringPlacement === 'SCHEDULE_NOW' ? (
                                    <FormField
                                      control={form.control}
                                      name="recurringTimeMode"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Visit times</FormLabel>
                                          <SegmentedControl
                                            value={field.value}
                                            options={[
                                              { value: 'SAME', label: 'Same every visit' },
                                              { value: 'PER_VISIT', label: 'Set per visit' },
                                            ]}
                                            onChange={field.onChange}
                                          />
                                        </FormItem>
                                      )}
                                    />
                                  ) : null}
                                  {recurringPlacement !== 'ROUTE_BASED' ? (
                                    <FormField
                                      control={form.control}
                                      name="recurringTechnicianMode"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Technician strategy</FormLabel>
                                          <SegmentedControl
                                            value={field.value}
                                            options={[
                                              { value: 'SAME', label: 'Same best tech' },
                                              { value: 'PER_VISIT', label: 'Best tech per visit' },
                                            ]}
                                            onChange={field.onChange}
                                          />
                                        </FormItem>
                                      )}
                                    />
                                  ) : null}
                                </div>

                                {recurringPatternMode === 'FIXED' &&
                                recurrenceFrequency === 'WEEK' ? (
                                  <div className="space-y-2">
                                    <p className="text-sm font-medium">Visit days</p>
                                    <div className="flex flex-wrap gap-2">
                                      {WEEKDAY_OPTIONS.map((day) => {
                                        const selected = recurringWeekDays.includes(day.value);
                                        return (
                                          <Button
                                            key={day.value}
                                            type="button"
                                            size="sm"
                                            variant={selected ? 'default' : 'outline'}
                                            onClick={() =>
                                              form.setValue(
                                                'recurringWeekDays',
                                                selected
                                                  ? recurringWeekDays.filter(
                                                      (value) => value !== day.value,
                                                    )
                                                  : [...recurringWeekDays, day.value].sort(),
                                                { shouldDirty: true, shouldValidate: true },
                                              )
                                            }
                                          >
                                            {day.label}
                                          </Button>
                                        );
                                      })}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                      The start date remains selectable on each new job.
                                    </p>
                                  </div>
                                ) : null}

                                {recurringPatternMode === 'FIXED' &&
                                recurrenceFrequency === 'MONTH' ? (
                                  <div className="grid gap-4 sm:grid-cols-3">
                                    <FormField
                                      control={form.control}
                                      name="recurringMonthMode"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Monthly rule</FormLabel>
                                          <Select
                                            value={field.value}
                                            onValueChange={field.onChange}
                                          >
                                            <FormControl>
                                              <SelectTrigger className="w-full">
                                                <SelectValue />
                                              </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                              <SelectItem value="DAY_OF_MONTH">
                                                Day of month
                                              </SelectItem>
                                              <SelectItem value="DAY_OF_WEEK">
                                                Weekday occurrence
                                              </SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </FormItem>
                                      )}
                                    />
                                    {recurringMonthMode === 'DAY_OF_MONTH' ? (
                                      <>
                                        <FormField
                                          control={form.control}
                                          name="recurringMonthDay"
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Day</FormLabel>
                                              <FormControl>
                                                <Input
                                                  type="number"
                                                  min={1}
                                                  max={31}
                                                  disabled={isPending || recurringMonthLastDay}
                                                  {...field}
                                                />
                                              </FormControl>
                                            </FormItem>
                                          )}
                                        />
                                        <FormField
                                          control={form.control}
                                          name="recurringMonthLastDay"
                                          render={({ field }) => (
                                            <FormItem className="flex items-end gap-2 pb-2">
                                              <Checkbox
                                                checked={field.value}
                                                onCheckedChange={(checked) =>
                                                  field.onChange(checked === true)
                                                }
                                              />
                                              <FormLabel>Use last day</FormLabel>
                                            </FormItem>
                                          )}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <FormField
                                          control={form.control}
                                          name="recurringMonthDayOfWeekOccurrence"
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Occurrence</FormLabel>
                                              <FormControl>
                                                <Input type="number" min={1} max={5} {...field} />
                                              </FormControl>
                                            </FormItem>
                                          )}
                                        />
                                        <FormField
                                          control={form.control}
                                          name="recurringMonthDayOfWeek"
                                          render={({ field }) => (
                                            <FormItem>
                                              <FormLabel>Weekday</FormLabel>
                                              <Select
                                                value={String(field.value)}
                                                onValueChange={(value) =>
                                                  field.onChange(Number(value))
                                                }
                                              >
                                                <FormControl>
                                                  <SelectTrigger className="w-full">
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                  {WEEKDAY_OPTIONS.map((day) => (
                                                    <SelectItem
                                                      key={day.value}
                                                      value={String(day.value)}
                                                    >
                                                      {day.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </FormItem>
                                          )}
                                        />
                                      </>
                                    )}
                                  </div>
                                ) : null}

                                {recurringPatternMode === 'FIXED' &&
                                recurrenceFrequency === 'YEAR' ? (
                                  <div className="grid gap-4 sm:max-w-md sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name="recurringAnnualMonth"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Month</FormLabel>
                                          <FormControl>
                                            <Input type="number" min={1} max={12} {...field} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name="recurringAnnualDay"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Day</FormLabel>
                                          <FormControl>
                                            <Input type="number" min={1} max={31} {...field} />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                ) : null}

                                {recurringPlacement === 'SCHEDULE_NOW' &&
                                recurringTimeMode === 'SAME' ? (
                                  <div className="grid gap-4 sm:max-w-md sm:grid-cols-2">
                                    <FormField
                                      control={form.control}
                                      name="recurringVisitStartTime"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Visit start</FormLabel>
                                          <FormControl>
                                            <Input
                                              type="time"
                                              disabled={isPending}
                                              value={field.value ?? ''}
                                              onChange={field.onChange}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name="recurringVisitEndTime"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>Visit end</FormLabel>
                                          <FormControl>
                                            <Input
                                              type="time"
                                              disabled={isPending}
                                              value={field.value ?? ''}
                                              onChange={field.onChange}
                                            />
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                ) : null}
                              </SchedulingBranch>
                            ) : null}
                            <p
                              className={cn(
                                'text-xs text-muted-foreground',
                                !editingRecurringRule && 'sm:ml-[192px]',
                              )}
                            >
                              {recurringPatternMode === 'CHOOSE_ON_CREATE'
                                ? 'Nothing is pre-filled. The dispatcher defines the recurrence on each new job.'
                                : recurringPatternMode === 'FLEXIBLE'
                                  ? `New jobs ask for ${recurringVisitsPerCycle} visit${recurringVisitsPerCycle === 1 ? '' : 's'} per cycle on eligible days${
                                      recurringPlacement === 'ROUTE_BASED'
                                        ? ' and let routing choose the technician and order without committing an arrival time.'
                                        : recurringPlacement === 'ALL_DAY'
                                          ? ' as flexible-day visits.'
                                          : ' and search for scheduled times.'
                                    }`
                                  : `New jobs open with a fixed ${recurrenceFrequency.toLowerCase()} recurrence; the start date is still chosen on the job.`}
                            </p>
                          </div>
                        </div>
                      </>
                    ) : null}

                    {/* Customer rescheduling rules (formerly a standalone
                        section — content unchanged). */}
                    <section className="space-y-4 border-t pt-5">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium">Customer rescheduling</p>
                        <HelpPopover label="About customer rescheduling">
                          Choose whether customers can only ask dispatch for a new time or move the
                          visit themselves using live booking availability.
                        </HelpPopover>
                      </div>
                      <FormField
                        control={form.control}
                        name="customerRescheduleMode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Customer access</FormLabel>
                            <SegmentedControl
                              value={field.value}
                              disabled={isPending}
                              options={[
                                { value: 'DISABLED', label: 'Disabled' },
                                { value: 'REQUEST', label: 'Request only' },
                                { value: 'SELF_SERVICE', label: 'Self-service' },
                              ]}
                              onChange={field.onChange}
                              className="!grid w-full grid-cols-3 [&>button]:w-full"
                            />
                            <p className="text-xs text-muted-foreground">
                              {field.value === 'DISABLED'
                                ? 'The management link will direct customers to contact the office.'
                                : field.value === 'REQUEST'
                                  ? 'The visit stays in place and dispatch receives a reschedule request.'
                                  : 'The customer chooses an available slot and the visit moves immediately.'}
                            </p>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {customerRescheduleMode !== 'DISABLED' ? (
                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="rescheduleCutoffHours"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Change cutoff (hours)</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={8760}
                                    disabled={isPending}
                                    {...field}
                                  />
                                </FormControl>
                                <p className="text-xs text-muted-foreground">
                                  Online changes close this many hours before arrival.
                                </p>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {customerRescheduleMode === 'SELF_SERVICE' ? (
                            <FormField
                              control={form.control}
                              name="maxCustomerReschedules"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Maximum self-service moves</FormLabel>
                                  <FormControl>
                                    <Input
                                      type="number"
                                      min={1}
                                      max={20}
                                      disabled={isPending}
                                      {...field}
                                    />
                                  </FormControl>
                                  <p className="text-xs text-muted-foreground">
                                    Counted per visit, even when another link is issued.
                                  </p>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ) : null}
                        </div>
                      ) : null}
                    </section>

                    {/* Visit cancellation policy (formerly a standalone
                        section — content unchanged). */}
                    <section className="space-y-4 border-t pt-5">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium">Visit cancellation policy</p>
                        <HelpPopover label="About visit cancellation policy">
                          Controls fees and deposit refunds when a customer cancels through their
                          booking link. For recurring jobs, this does not cancel the full contract.
                        </HelpPopover>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="cancellationWindowHours"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Free-cancel window (hours)</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  placeholder="e.g. 24"
                                  disabled={isPending}
                                  {...field}
                                  value={field.value ?? ''}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="cancellationFeeType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Fee type</FormLabel>
                              <Select
                                value={field.value}
                                disabled={isPending}
                                onValueChange={field.onChange}
                              >
                                <FormControl>
                                  <SelectTrigger className="w-full">
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="NONE">None</SelectItem>
                                  <SelectItem value="FLAT">Flat ($)</SelectItem>
                                  <SelectItem value="PERCENT">Percent (%)</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {form.watch('cancellationFeeType') !== 'NONE' ? (
                        <FormField
                          control={form.control}
                          name="cancellationFeeAmount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {form.watch('cancellationFeeType') === 'PERCENT'
                                  ? 'Fee percent (0–100)'
                                  : 'Fee amount ($)'}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  max={
                                    form.watch('cancellationFeeType') === 'PERCENT'
                                      ? 100
                                      : undefined
                                  }
                                  step="0.01"
                                  placeholder={
                                    form.watch('cancellationFeeType') === 'PERCENT' ? '10' : '0.00'
                                  }
                                  disabled={isPending}
                                  {...field}
                                  value={field.value ?? ''}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      ) : null}

                      <FormField
                        control={form.control}
                        name="depositRefundable"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between gap-4 rounded-md border p-3">
                            <FormLabel className="font-medium">Refund deposit on cancel</FormLabel>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                disabled={isPending}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </section>
                  </FormAccordionCard>

                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={Settings}
                        label="Notifications"
                        badge={
                          liveValues.schedulingNotificationPolicy === 'ASK'
                            ? 'Ask on job creation'
                            : undefined
                        }
                        open={openSections.notifications}
                        onToggle={() => toggleSection('notifications')}
                      />
                    }
                    sectionId="notifications"
                    open={openSections.notifications}
                    bodyClassName="space-y-4"
                  >
                    <FormField
                      control={form.control}
                      name="schedulingNotificationPolicy"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>When creating a scheduled job</FormLabel>
                          <Select
                            value={field.value}
                            disabled={isPending}
                            onValueChange={field.onChange}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="ASK">Ask on job creation</SelectItem>
                              <SelectItem value="NONE">Don't send notifications</SelectItem>
                              <SelectItem value="SMS">Send SMS</SelectItem>
                              <SelectItem value="EMAIL">Send email</SelectItem>
                              <SelectItem value="BOTH">Send SMS and email</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Default for scheduling and confirmation notifications. Jobs inherit this
                            choice.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </FormAccordionCard>

                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={Settings}
                        label="Workflow defaults"
                        badge={
                          workflowOverrideCount > 0
                            ? `${workflowOverrideCount} overridden`
                            : 'optional'
                        }
                        open={openSections.workflow}
                        onToggle={() => toggleSection('workflow')}
                      />
                    }
                    sectionId="workflow"
                    open={openSections.workflow}
                    bodyClassName="space-y-0"
                  >
                    {supportsOneOff ? (
                      <section className="space-y-4 pb-5">
                        <div className="grid gap-4 md:grid-cols-2">
                          <WorkflowToggleField
                            control={form.control}
                            name="autoInvoice"
                            label="Auto-invoice on completion"
                            description="Available only for fixed-price one-off jobs."
                            defaultLabel="Job default · off"
                            isPending={isPending}
                          />
                        </div>
                      </section>
                    ) : null}

                    <section className={cn('space-y-4 py-5', supportsOneOff && 'border-t')}>
                      <p className="text-sm font-medium">Payment & follow-up</p>
                      <div className="grid gap-4 md:grid-cols-2">
                        <WorkflowToggleField
                          control={form.control}
                          name="autoCharge"
                          label="Auto-charge"
                          description="Requires the customer to have a saved payment method before the job can be saved."
                          defaultLabel="Job default · off"
                          isPending={isPending}
                        />
                        <WorkflowToggleField
                          control={form.control}
                          name="reviewRequestDefault"
                          label="Send review request"
                          description="Sends a review request after the job is marked completed."
                          defaultLabel="Job default · off"
                          isPending={isPending}
                        />
                      </div>
                    </section>

                    {supportsRecurring ? (
                      <section className="space-y-4 border-t pt-5">
                        <div className="grid gap-4 md:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="invoicingMethod"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center gap-1.5">
                                  <FormLabel>Invoice amount</FormLabel>
                                  <HelpPopover label="About invoice amount">
                                    Controls whether an invoice uses completed visit charges or the
                                    fixed total of the job.
                                  </HelpPopover>
                                </div>
                                <Select
                                  value={field.value ?? 'default'}
                                  disabled={isPending}
                                  onValueChange={(value) =>
                                    field.onChange(value === 'default' ? null : value)
                                  }
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={cn(
                                        'w-full',
                                        field.value != null && 'border-foreground font-medium',
                                      )}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="default">
                                      From the job pricing model
                                    </SelectItem>
                                    {INVOICING_METHOD_OPTIONS.map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="invoicingSchedule"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center gap-1.5">
                                  <FormLabel>Invoice timing</FormLabel>
                                  <HelpPopover label="About invoice timing">
                                    Monthly billing batches completed, uninvoiced visits into one
                                    invoice.
                                  </HelpPopover>
                                </div>
                                <Select
                                  value={
                                    field.value == null || field.value === 'ON_COMPLETION'
                                      ? 'default'
                                      : field.value
                                  }
                                  disabled={isPending}
                                  onValueChange={(value) =>
                                    field.onChange(value === 'default' ? null : value)
                                  }
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={cn(
                                        'w-full',
                                        field.value != null && 'border-foreground font-medium',
                                      )}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="default">
                                      After each completed visit
                                    </SelectItem>
                                    {INVOICING_SCHEDULE_OPTIONS.map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                    {legacyInvoicingSchedule ? (
                                      <SelectItem value={legacyInvoicingSchedule} disabled>
                                        {legacyInvoicingSchedule === 'CUSTOM_SCHEDULE'
                                          ? 'Legacy custom schedule'
                                          : 'Legacy as-needed schedule'}
                                      </SelectItem>
                                    ) : null}
                                  </SelectContent>
                                </Select>
                                {legacyInvoicingSchedule ? (
                                  <p className="text-xs text-amber-700">
                                    This older setting is preserved, but it cannot be configured
                                    completely here. Choose a supported timing to replace it.
                                  </p>
                                ) : null}
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="grid gap-4 border-t pt-4 md:grid-cols-[minmax(0,1fr)_minmax(280px,0.8fr)] md:items-end">
                          <div className="flex items-center gap-1.5 self-center">
                            <p className="text-sm font-medium">Recurring weather handling</p>
                            <HelpPopover label="About recurring weather handling">
                              Controls what scheduling does when weather interrupts a recurring
                              visit. This is an operational rule, not a field shown on the new-job
                              form.
                            </HelpPopover>
                          </div>
                          <FormField
                            control={form.control}
                            name="weatherPolicy"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Interrupted visits</FormLabel>
                                <Select
                                  value={field.value ?? 'inherit'}
                                  disabled={isPending}
                                  onValueChange={(value) =>
                                    field.onChange(value === 'inherit' ? null : value)
                                  }
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={cn(
                                        'w-full',
                                        field.value != null && 'border-foreground font-medium',
                                      )}
                                    >
                                      <SelectValue />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    <SelectItem value="inherit">
                                      Automatic · subscription skips, other billing makes up
                                    </SelectItem>
                                    {WEATHER_POLICY_OPTIONS.map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </section>
                    ) : null}

                    <section className="space-y-3 border-t pt-5">
                      <FormField
                        control={form.control}
                        name="tagIds"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center gap-1.5">
                              <FormLabel>Job tags</FormLabel>
                              <HelpPopover label="About job tags">
                                Every job created with this type carries these tags — whoever books
                                it (dashboard, online booking or Iris). They show on the job, the
                                dispatch board and the calendar, and drive job-list filters.
                              </HelpPopover>
                            </div>
                            <FormControl>
                              <TagSelector
                                variant="input"
                                scope="job"
                                triggerId="job-type-tags"
                                value={field.value ?? []}
                                onChange={field.onChange}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </section>
                  </FormAccordionCard>

                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={ShieldCheck}
                        label="Authorization & Sign-off"
                        badge="optional"
                        extra={
                          <HelpPopover label="About authorization and sign-off">
                            New jobs inherit these requirements. Work cannot start or be completed
                            until the required customer approval is collected.
                          </HelpPopover>
                        }
                        open={openSections.gates}
                        onToggle={() => toggleSection('gates')}
                      />
                    }
                    sectionId="gates"
                    open={openSections.gates}
                    bodyClassName="space-y-5"
                  >
                    <div className="grid gap-4 md:grid-cols-2 md:items-start">
                      <div className="space-y-3">
                        <FormField
                          control={form.control}
                          name="authRequired"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between gap-4 rounded-md border p-3">
                              <FormLabel className="font-medium">
                                Require work authorization
                              </FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  disabled={isPending}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                    if (checked) {
                                      form.setValue('authMethod', 'SIGNATURE', {
                                        shouldDirty: true,
                                      });
                                    }
                                  }}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="space-y-3">
                        <FormField
                          control={form.control}
                          name="signoffRequired"
                          render={({ field }) => (
                            <FormItem className="flex items-center justify-between gap-4 rounded-md border p-3">
                              <FormLabel className="font-medium">
                                Require completion sign-off
                              </FormLabel>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  disabled={isPending}
                                  onCheckedChange={(checked) => {
                                    field.onChange(checked);
                                    if (checked) {
                                      form.setValue('signoffMethod', 'SIGNATURE', {
                                        shouldDirty: true,
                                      });
                                    }
                                  }}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </FormAccordionCard>

                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={ScrollText}
                        label="Terms & Conditions"
                        badge="optional"
                        statusBadge={liveValues.termsMode !== 'DEFAULT' ? 'Overridden' : undefined}
                        extra={
                          <HelpPopover label="About terms & conditions">
                            Jobs created with this type start on these terms. The job can still
                            switch to another template or write custom terms before it goes out.
                          </HelpPopover>
                        }
                        open={openSections.terms}
                        onToggle={() => toggleSection('terms')}
                      />
                    }
                    sectionId="terms"
                    open={openSections.terms}
                    bodyClassName="space-y-4"
                  >
                    <TermsSelector
                      value={{
                        termsMode: liveValues.termsMode,
                        termsTemplateIds: liveValues.termsTemplateIds,
                        termsAndConditions: liveValues.termsAndConditions,
                      }}
                      disabled={isPending}
                      onChange={(next) => {
                        form.setValue('termsMode', next.termsMode, { shouldDirty: true });
                        form.setValue('termsTemplateIds', next.termsTemplateIds, {
                          shouldDirty: true,
                        });
                        form.setValue('termsAndConditions', next.termsAndConditions, {
                          shouldDirty: true,
                        });
                      }}
                    />
                  </FormAccordionCard>

                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={Wrench}
                        label="Required skills"
                        badge="required"
                        open={openSections.skills}
                        onToggle={() => toggleSection('skills')}
                      />
                    }
                    sectionId="skills"
                    open={openSections.skills}
                    bodyClassName="space-y-0"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3 pb-4">
                      <p
                        className={cn(
                          'text-sm',
                          skillsError ? 'font-medium text-destructive' : 'text-muted-foreground',
                        )}
                      >
                        {skillsError
                          ? skillsError
                          : anyTechnician
                            ? 'Any active technician can take this job type.'
                            : specificSkillIds.length === 0
                              ? REQUIRED_SKILL_MESSAGE
                              : specificSkillIds.length === 1
                                ? 'The technician needs the selected skill.'
                                : `The technician needs all ${specificSkillIds.length} selected skills.`}
                      </p>
                      <Badge className="border-0 bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-800 hover:bg-emerald-50">
                        {techniciansLoading
                          ? 'Checking technician coverage...'
                          : `${qualifiedTechnicianCount} of ${activeTechnicians.length} techs qualify`}
                      </Badge>
                    </div>

                    <label
                      htmlFor="job-type-any-technician"
                      className="flex items-start gap-3 border-b pb-4 text-sm"
                    >
                      <Checkbox
                        id="job-type-any-technician"
                        checked={anyTechnician}
                        disabled={isPending}
                        aria-label="Any active technician can take this job type"
                        onCheckedChange={(checked) => toggleAnyTechnician(checked === true)}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="font-medium">No specific skill required</span>
                        <span className="block text-muted-foreground">
                          Any active technician qualifies. Turning this on clears the skills below —
                          a job type is dispatched either by skill or to everyone, not both.
                        </span>
                      </span>
                    </label>

                    {anyTechnician ? null : selectedSkillRows.length > 0 ? (
                      <div>
                        {selectedSkillRows.map((skill) => (
                          <div
                            key={skill.id}
                            className="grid min-h-[64px] grid-cols-[minmax(0,1fr)_40px] items-center gap-4 border-b py-3"
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <span
                                className={cn(
                                  'h-2.5 w-2.5 shrink-0 rounded-full',
                                  tradeDotClass(skill.trade?.color),
                                )}
                              />
                              <span className="truncate text-base font-medium">{skill.name}</span>
                              <span className="truncate text-sm text-muted-foreground">
                                {skill.trade?.name ?? 'Uncategorized'}
                              </span>
                            </div>

                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              className="h-10 w-10"
                              disabled={isPending}
                              aria-label={`Remove ${skill.name}`}
                              onClick={() => removeSkill(skill.id)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="py-6 text-sm text-muted-foreground">
                        No skills added yet. Add one below, or tick “No specific skill required”
                        above — this job type cannot be saved without one or the other.
                      </p>
                    )}

                    <div className="flex flex-wrap items-center gap-4 pt-4">
                      <Popover open={skillsPickerOpen} onOpenChange={setSkillsPickerOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className="border-dashed"
                            disabled={isPending || anyTechnician}
                          >
                            <Plus className="h-4 w-4" />
                            Add skill
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search skills..." />
                            <CommandList>
                              <CommandEmpty>No available skills found.</CommandEmpty>
                              <CommandGroup>
                                {availableSkills.map((skill) => (
                                  <CommandItem
                                    key={skill.id}
                                    value={`${skill.name} ${skill.trade?.name ?? ''}`}
                                    onSelect={() => addSkill(skill.id)}
                                  >
                                    <span
                                      className={cn(
                                        'h-2.5 w-2.5 shrink-0 rounded-full',
                                        tradeDotClass(skill.trade?.color),
                                      )}
                                    />
                                    <span className="min-w-0 flex-1 truncate">{skill.name}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {skill.trade?.name ?? 'Uncategorized'}
                                    </span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                              <CommandSeparator />
                              <CommandGroup>
                                <CommandItem
                                  forceMount
                                  value="create new skill"
                                  className="font-medium"
                                  onSelect={() => {
                                    setSkillsPickerOpen(false);
                                    setSkillCreateOpen(true);
                                  }}
                                >
                                  <Plus className="h-4 w-4" />
                                  Create skill
                                </CommandItem>
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </FormAccordionCard>

                  <FormAccordionCard
                    header={
                      <CollapsibleSectionHeader
                        icon={ClipboardList}
                        label="Linked forms"
                        badge="optional"
                        open={openSections.checklists}
                        onToggle={() => toggleSection('checklists')}
                      />
                    }
                    sectionId="checklists"
                    open={openSections.checklists}
                    bodyClassName="space-y-0"
                  >
                    {checklistsLoading ? (
                      <p className="text-sm text-muted-foreground">Loading forms...</p>
                    ) : (
                      <>
                        {selectedChecklistRows.length > 0 ? (
                          <DndContext
                            sensors={linkSensors}
                            collisionDetection={closestCenter}
                            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                            onDragEnd={moveChecklistLink}
                          >
                            <SortableContext
                              items={selectedChecklistRows.map(({ definition }) => definition.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="space-y-1">
                                {selectedChecklistRows.map(({ definition, link }) => (
                                  <SortableLinkRow
                                    key={definition.id}
                                    id={definition.id}
                                    label={definition.name}
                                    disabled={isPending}
                                    className="grid min-h-[56px] items-center gap-3 rounded-md bg-muted/40 px-3 py-2 sm:grid-cols-[20px_minmax(0,1fr)_auto_40px]"
                                  >
                                    <div className="min-w-0">
                                      {/* Name on its own line, its version and kind
                                          beneath — the same shape on every row,
                                          whatever the name's length. */}
                                      <p className="truncate text-sm font-medium">
                                        {definition.name}
                                      </p>
                                      <div className="mt-1 flex items-center gap-1.5">
                                        <Badge variant="outline" className="text-[10px]">
                                          v{definition.version ?? 1}
                                        </Badge>
                                      </div>
                                      {definition.description ? (
                                        <p className="mt-1 truncate text-xs text-muted-foreground">
                                          {definition.description}
                                        </p>
                                      ) : null}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                                      <label className="flex h-9 items-center gap-2 text-sm">
                                        <span className="text-muted-foreground">Add</span>
                                        <Select
                                          value={link.autoAttach ?? NO_AUTO_ATTACH}
                                          disabled={isPending}
                                          onValueChange={(next) =>
                                            updateChecklistDraft(definition.id, {
                                              autoAttach:
                                                next === NO_AUTO_ATTACH
                                                  ? null
                                                  : (next as FormAutoAttach),
                                            })
                                          }
                                        >
                                          <SelectTrigger
                                            className="h-9 w-[176px]"
                                            aria-label={`When to add ${definition.name}`}
                                          >
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="VISIT">On every visit</SelectItem>
                                            <SelectItem value="JOB">Once per job</SelectItem>
                                            <SelectItem
                                              value={NO_AUTO_ATTACH}
                                              disabled={link.blocksCompletion}
                                            >
                                              Available to add
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </label>
                                      <label className="flex h-9 items-center gap-2 text-sm">
                                        <span className="text-muted-foreground">Completed by</span>
                                        <Select
                                          value={completedByValue(
                                            link.audience,
                                            definition.publicFillable,
                                          )}
                                          disabled={isPending}
                                          onValueChange={(next) =>
                                            updateChecklistDraft(definition.id, {
                                              audience: next as FormAudience,
                                            })
                                          }
                                        >
                                          <SelectTrigger
                                            className="h-9 w-[140px]"
                                            aria-label={`Who completes ${definition.name}`}
                                          >
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="TECHNICIAN">Technician</SelectItem>
                                            <SelectItem value="CUSTOMER">Customer</SelectItem>
                                            <SelectItem value="OFFICE">Office</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </label>
                                      <label className="flex h-9 items-center gap-3 text-sm">
                                        <span>Blocks completion</span>
                                        <Switch
                                          checked={link.blocksCompletion}
                                          disabled={isPending}
                                          onCheckedChange={(checked) =>
                                            updateChecklistDraft(definition.id, {
                                              blocksCompletion: checked,
                                              // Required means it is there: one left to be
                                              // added by hand now starts on the job.
                                              ...(checked && link.autoAttach == null
                                                ? { autoAttach: 'JOB' as const }
                                                : {}),
                                            })
                                          }
                                        />
                                      </label>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="icon"
                                      className="h-10 w-10"
                                      disabled={isPending}
                                      aria-label={`Remove ${definition.name}`}
                                      onClick={() => removeChecklistDefinition(definition.id)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </SortableLinkRow>
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        ) : (
                          <div className="rounded-lg border border-dashed px-6 py-10 text-center">
                            <ClipboardList className="mx-auto h-6 w-6 text-muted-foreground" />
                            <p className="mt-3 text-sm font-medium">No forms linked yet</p>
                            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                              Anything linked here is added to every new job of this type, ready for
                              the technician or the customer to complete.
                            </p>
                          </div>
                        )}

                        <div className="flex flex-wrap items-center gap-3 pt-4">
                          <Popover
                            open={checklistsPickerOpen}
                            onOpenChange={setChecklistsPickerOpen}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="border-dashed"
                                disabled={isPending}
                              >
                                <Plus className="h-4 w-4" />
                                Add existing
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search forms..." />
                                <CommandList>
                                  <CommandEmpty>No available forms found.</CommandEmpty>
                                  <CommandGroup>
                                    {availableChecklistDefinitions.map((definition) => (
                                      <CommandItem
                                        key={definition.id}
                                        value={`${definition.name} ${definition.description ?? ''}`}
                                        onSelect={() => addChecklistDefinition(definition.id)}
                                      >
                                        <ClipboardList className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <span className="min-w-0 flex-1 truncate">
                                          {definition.name}
                                        </span>
                                        <Badge variant="outline" className="text-[10px]">
                                          v{definition.version ?? 1}
                                        </Badge>
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          <Button
                            type="button"
                            variant="outline"
                            className="border-dashed"
                            disabled={isPending}
                            onClick={() => setChecklistCreateSurface('VISIT_FORM')}
                          >
                            <Plus className="h-4 w-4" />
                            Create form
                          </Button>
                        </div>
                      </>
                    )}
                  </FormAccordionCard>
                </>
              )}
            </div>
          </div>

          {!isPageLayout ? (
            <SheetFooter className="flex-row justify-end border-t px-5 py-4">
              <Button type="button" variant="outline" onClick={onBack ?? onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isPending || (isEdit ? !jobType || !hasChanges : !form.formState.isValid)}
              >
                {isPending ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save' : 'Create'}
              </Button>
            </SheetFooter>
          ) : null}
        </form>
      </Form>
      <SkillCreateDialog
        open={skillCreateOpen}
        onOpenChange={setSkillCreateOpen}
        onCreated={handleSkillCreated}
      />
      <UnifiedChecklistCreateSheet
        open={checklistCreateSurface !== null}
        onOpenChange={(next) => {
          if (!next) setChecklistCreateSurface(null);
        }}
        surface={checklistCreateSurface ?? 'VISIT_FORM'}
        askRequired
        onCreated={handleChecklistCreated}
      />
    </div>
  );
}

/**
 * A linked record's row, moved by its handle. Dragging only from the grip keeps
 * the selects and switch on the row usable; the keyboard sensor moves it with
 * space and the arrow keys.
 */
function SortableLinkRow({
  id,
  label,
  disabled,
  className,
  children,
}: {
  id: string;
  label: string;
  disabled: boolean;
  className: string;
  children: ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(className, isDragging && 'relative z-10 shadow-md')}
    >
      <button
        ref={setActivatorNodeRef}
        type="button"
        aria-label={`Drag ${label} to reorder`}
        disabled={disabled}
        className="flex h-9 w-5 cursor-grab touch-none items-center justify-center rounded text-muted-foreground hover:text-foreground active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-50"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      {children}
    </div>
  );
}

/** Select has no null, and "don't create it" is a real choice here. */
const NO_AUTO_ATTACH = 'manual';

/**
 * The value the picker shows. A link that predates the choice — or carries the
 * legacy 'ANY' — falls back to the template's own setting, which is what
 * actually gates the public form route the customer arrives through.
 */
function completedByValue(
  audience: FormAudience | undefined,
  publicFillable: boolean | undefined,
): FormAudience {
  if (audience === 'CUSTOMER' || audience === 'TECHNICIAN' || audience === 'OFFICE') {
    return audience;
  }
  return publicFillable ? 'CUSTOMER' : 'TECHNICIAN';
}

function formatDuration(rawMinutes: number | string) {
  const minutes = Number(rawMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return 'Not set';
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder} min`;
  if (remainder === 0) return `${hours} h`;
  return `${hours} h ${remainder} min`;
}

function HelpPopover({
  label = 'More information',
  children,
}: {
  label?: string;
  children: ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3 text-xs leading-5 text-muted-foreground">
        {children}
      </PopoverContent>
    </Popover>
  );
}

function WorkflowToggleField({
  control,
  name,
  label,
  description,
  defaultLabel,
  isPending,
}: {
  control: Control<JobTypeFormValues, unknown, JobTypeFormValues>;
  name: 'autoInvoice' | 'autoCharge' | 'reviewRequestDefault';
  label: string;
  description: string;
  defaultLabel: string;
  isPending: boolean;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className="min-w-0">
          <div className="flex min-h-[22px] items-center gap-1.5">
            <FormLabel>{label}</FormLabel>
            <HelpPopover label={`About ${label}`}>{description}</HelpPopover>
          </div>
          <div className="-mt-[7px] grid grid-cols-3 rounded-md border bg-muted/40 p-0.5">
            {[
              { value: 'inherit', label: defaultLabel },
              { value: 'on', label: 'Always' },
              { value: 'off', label: 'Never' },
            ].map((option) => {
              const selectedValue = field.value == null ? 'inherit' : field.value ? 'on' : 'off';
              return (
                <button
                  key={option.value}
                  type="button"
                  disabled={isPending}
                  className={cn(
                    'h-[30px] min-w-0 whitespace-nowrap rounded px-2 text-sm text-muted-foreground transition-colors',
                    selectedValue === option.value &&
                      'bg-background font-semibold text-foreground shadow-sm',
                  )}
                  onClick={() =>
                    field.onChange(option.value === 'inherit' ? null : option.value === 'on')
                  }
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </FormItem>
      )}
    />
  );
}

// Exported for contract tests: a new Job Type is born with Iris DEACTIVATED
// (irisMode NOT_LISTED server-side) — Iris does not know the service exists
// until the tenant deliberately activates it.
// eslint-disable-next-line react-refresh/only-export-components
export function createDefaults(): JobTypeFormValues {
  return {
    name: '',
    shortCode: '',
    color: PRESET_COLORS[0],
    icon: DEFAULT_JOB_TYPE_ICON_NAME,
    pipelineId: null,
    businessUnitIds: [],
    tagIds: [],
    description: '',
    customerPhrases: '',
    irisActivated: false,
    isDiagnostic: false,
    // Visit-price rule default: credit the visit price on the resulting job.
    serviceCallFeePolicy: 'WAIVE_ON_CALL',
    visitDepositEnabled: false,
    visitDepositType: 'FLAT',
    visitDepositValue: 0,
    irisSurveyQuestions: [],
    irisSurveyTiming: 'BEFORE_SLOTS',
    irisAskEquipment: false,
    irisCustomerDescription: '',
    irisMatchingExclusions: '',
    irisIncludedWork: '',
    irisExcludedWork: '',
    irisFaqs: '',
    irisWarranty: '',
    irisAdditionalGuidance: '',
    onDutySchedulingEnabled: false,
    requiresParts: false,
    defaultDuration: 60,
    defaultPriority: 'NORMAL',
    minCrewSize: 1,
    recurrenceFrequency: 'WEEK',
    recurrenceInterval: 1,
    schedulingAvailability: 'ONE_OFF',
    oneOffPatternMode: 'CHOOSE_ON_CREATE',
    oneOffScheduleMode: 'SCHEDULE_NOW',
    oneOffVisitShape: 'TIMED',
    oneOffPreferredTimeWindow: 'ANY',
    oneOffSpecificStartTime: null,
    oneOffConsecutiveDays: 2,
    oneOffUrgency: 'STANDARD',
    recurringPatternMode: 'CHOOSE_ON_CREATE',
    recurringVisitsPerCycle: 1,
    recurringDayConstraintMode: 'ANY',
    recurringAllowedWeekDays: [],
    recurringPlacement: 'SCHEDULE_NOW',
    recurringWeekDays: [],
    recurringMonthMode: 'DAY_OF_MONTH',
    recurringMonthDay: 1,
    recurringMonthLastDay: false,
    recurringMonthDayOfWeek: 1,
    recurringMonthDayOfWeekOccurrence: 1,
    recurringAnnualMonth: 1,
    recurringAnnualDay: 1,
    recurringTimeMode: 'SAME',
    recurringTechnicianMode: 'PER_VISIT',
    recurringVisitStartTime: '09:00',
    recurringVisitEndTime: '10:00',
    authRequired: false,
    authMethod: 'SIGNATURE',
    signoffRequired: false,
    signoffMethod: 'SIGNATURE',
    cancellationWindowHours: '',
    cancellationFeeType: 'NONE',
    cancellationFeeAmount: '',
    depositRefundable: true,
    customerRescheduleMode: 'REQUEST',
    rescheduleCutoffHours: 24,
    maxCustomerReschedules: 1,
    // NOT-362 — workflow defaults start "not set" (null) so a job type that
    // never opens the Workflow Defaults section persists NULL, not a value —
    // except visit confirmation: new job types default to SMS + Email (the
    // user can still change or clear it).
    defaultStageId: null,
    schedulingNotificationPolicy: 'ASK',
    confirmationMethod: 'BOTH',
    invoicingMethod: null,
    invoicingSchedule: null,
    autoInvoice: null,
    autoCharge: null,
    reviewRequestDefault: null,
    weatherPolicy: null,
    ...EMPTY_TERMS_SELECTION,
  };
}

function areSkillSetsEqual(left: Set<string>, right: Set<string>) {
  if (left.size !== right.size) return false;
  for (const id of left) {
    if (!right.has(id)) return false;
  }
  return true;
}

function areLineItemsEqual(left: EditableLineItem[], right: EditableLineItem[]) {
  if (left.length !== right.length) return false;
  // Compare the price-defining fields; ignore the client-only _rowId which is
  // regenerated on every hydration.
  const signature = (items: EditableLineItem[]) =>
    JSON.stringify(
      items.map((item) => ({
        lineItemId: item.lineItemId,
        name: item.name,
        type: item.type,
        unit: item.unit,
        description: item.description ?? '',
        basePrice: item.basePrice,
        laborRate: item.laborRate,
        costBasis: item.costBasis,
        markupPercent: item.markupPercent,
        priceStrategy: item.priceStrategy,
        taxable: item.taxable,
        quantity: item.quantity,
        isOptional: item.isOptional,
        isSelected: item.isSelected,
        estimatedHours: item.estimatedHours ?? null,
      })),
    );

  return signature(left) === signature(right);
}

function areChecklistLinksEqual(left: ChecklistLinkDraft[], right: ChecklistLinkDraft[]) {
  if (left.length !== right.length) return false;
  const normalize = (links: ChecklistLinkDraft[]) =>
    links
      .map((link) => ({
        checklistDefinitionId: link.checklistDefinitionId,
        blocksCompletion: link.blocksCompletion,
        sortOrder: link.sortOrder,
        audience: link.audience,
        instantiationOverride: link.instantiationOverride,
        autoAttach: link.autoAttach ?? null,
      }))
      .sort((a, b) => a.checklistDefinitionId.localeCompare(b.checklistDefinitionId));

  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right));
}
